import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatDate } from '@/lib/constants';
import { MATERIALS, SURFACE_TREATMENTS, TOLERANCE_CLASSES, MANUFACTURING_PROCESSES, CERTIFICATIONS, PART_SPECIAL_POINTS, SP_CHIP_CLS, getSpecialPoint } from '@/lib/materials';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import FilePreviewDialog from '@/components/FilePreviewDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Eye, Upload, FileIcon, X, Loader2, Package, XCircle, MessageSquare, Send, Pencil, Sparkles, Target, ShieldCheck, ShieldAlert, CheckCircle2, Box, Forward, Search, Mail, RotateCcw, AlertTriangle, Inbox, Megaphone, Trophy, Ban, Clock, ClipboardList } from 'lucide-react';
import ForwardPRDialog from '@/components/ForwardPRDialog';
import NegotiationDialog from '@/components/NegotiationDialog';
import QuoteDeadlineBadge from '@/components/QuoteDeadlineBadge';
import { toast } from 'sonner';
import { FileList } from '@/components/FileManager';
import useFocusItem from '@/hooks/useFocusItem';

const EMPTY_PART = { name: '', material: '', designation: '', quantity: '', surface_treatment: 'Aucun', color_ral: '', tolerance: 'Selon plan', process: '', notes: '', special_points: [], ai_plan_summary_fr: '', ai_plan_summary_en: '' };
const EMPTY_FORM = {
  client_name: '', description: '', desired_deadline: '', quote_deadline: '', certifications: [], parts: [{ ...EMPTY_PART }],
  admin_link: '', price_target: '', laser_marking_enabled: false, laser_marking_text: '',
  general_tolerance: '', surface_finish: '', heat_treatment: '', marking_serialization: '',
  inspection_norm: '', packaging: '', production_type: '', additional_specs: '',
  order_already_placed: false,
};

// Items with "color" flag that require RAL/colour input
const COLOR_TREATMENTS = SURFACE_TREATMENTS.filter(s => s.color).map(s => s.fr);

// Auto-deletion retention policy (mirrors backend price_requests.py)
const RETENTION_DAYS = { proposition_faite: 30, client_declined: 7 };

function RetentionBadge({ req }) {
  const days = RETENTION_DAYS[req?.status];
  if (!days) return null;
  const anchor = req.status === 'proposition_faite' ? req.proposition_made_at : req.client_declined_at;
  if (!anchor) return null;
  const anchorDate = new Date(anchor);
  if (isNaN(anchorDate.getTime())) return null;
  const deleteAt = new Date(anchorDate.getTime() + days * 24 * 60 * 60 * 1000);
  const remaining = Math.ceil((deleteAt - Date.now()) / (24 * 60 * 60 * 1000));
  if (remaining <= 0) return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-rose-500/15 text-rose-300 border-rose-500/40 font-medium" title="Sera supprimée au prochain passage du nettoyage nocturne (04h)" data-testid={`retention-${req.id}`}>
      🕓 Suppression imminente
    </span>
  );
  const cls = remaining <= 3
    ? 'bg-rose-500/10 text-rose-300 border-rose-500/30'
    : remaining <= 7
      ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
      : 'bg-zinc-800/40 text-zinc-400 border-zinc-700';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${cls}`} title={`Auto-suppression le ${deleteAt.toLocaleDateString('fr-FR')} · politique : ${days} j après ${req.status === 'proposition_faite' ? 'proposition faite' : 'refus client'}`} data-testid={`retention-${req.id}`}>
      🕓 J-{remaining}
    </span>
  );
}

// Tab configuration — groups statuses into clean buckets for the admin
const PR_TABS = [
  { key: 'pending_review', label: 'À valider',           icon: ShieldAlert, statuses: ['pending_review'],                  accent: 'amber'   },
  { key: 'open',           label: 'En consultation',     icon: Inbox,       statuses: ['open'],                            accent: 'emerald' },
  { key: 'negotiation',    label: 'Négociation',         icon: MessageSquare, statuses: [], custom: 'negotiation',         accent: 'amber'   },
  { key: 'proposition',    label: 'Propositions client', icon: Megaphone,   statuses: ['proposition_faite'],               accent: 'violet'  },
  { key: 'won',            label: 'Gagnées · Fermées',   icon: Trophy,      statuses: ['closed'],                          accent: 'zinc'    },
  { key: 'refused',        label: 'Refusées',            icon: Ban,         statuses: ['client_declined', 'rejected_by_admin'], accent: 'rose' },
  { key: 'trash',          label: 'Corbeille',           icon: Trash2,      statuses: ['trashed'],                         accent: 'zinc'    },
];

const TAB_ACCENTS = {
  amber:   { dot: 'bg-amber-400',   active: 'text-amber-300 border-amber-500/40 bg-amber-500/10',   badge: 'bg-amber-500/20 text-amber-300' },
  emerald: { dot: 'bg-emerald-400', active: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', badge: 'bg-emerald-500/20 text-emerald-300' },
  violet:  { dot: 'bg-violet-400',  active: 'text-violet-300 border-violet-500/40 bg-violet-500/10', badge: 'bg-violet-500/20 text-violet-300' },
  rose:    { dot: 'bg-rose-400',    active: 'text-rose-300 border-rose-500/40 bg-rose-500/10',     badge: 'bg-rose-500/20 text-rose-300' },
  zinc:    { dot: 'bg-zinc-400',    active: 'text-zinc-100 border-zinc-500/40 bg-zinc-500/10',     badge: 'bg-zinc-700/60 text-zinc-200' },
};

export default function PriceRequestsPage() {
  const [requests, setRequests] = useState([]);
  useFocusItem(requests);
  const [trashedRequests, setTrashedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPR, setEditingPR] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [partFiles, setPartFiles] = useState({});
  // Opt-in AI analysis on file upload. Default OFF so the admin controls
  // when to burn IA quota / wait 30-45 s per plan. Manual "Analyser" button
  // remains available on each part when this is off.
  const [aiAutoAnalyze, setAiAutoAnalyze] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailFiles, setDetailFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('open');
  // Chat state (admin)
  const [chatRequest, setChatRequest] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThreadSubId, setActiveThreadSubId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatText, setNewChatText] = useState('');
  const [chatPendingFiles, setChatPendingFiles] = useState([]); // [{id, original_filename, size}]
  const [chatUploading, setChatUploading] = useState(false);
  // File preview (PDF / 3D STEP / images) — used by chat attachments
  const [previewFile, setPreviewFile] = useState(null);
  // Manual broadcast: after creating a PR, prompt the admin to pick which
  // subcontractors should receive the rich quote-ready email.
  const [broadcastPR, setBroadcastPR] = useState(null);
  const [broadcastLogPR, setBroadcastLogPR] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState(new Set()); // Set of PR IDs
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState('');
  const [merging, setMerging] = useState(false);
  // "Proposition faite" — open a dialog to pick the best-placed subcontractor
  const [proposalPR, setProposalPR] = useState(null);
  // Per-part AI plan summary (map partIdx → bool "analyzing")
  const [aiSummarizing, setAiSummarizing] = useState({});
  // Per-part active language for the AI summary display (fr | en)
  const [aiSummaryLang, setAiSummaryLang] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      // Fetch the non-trashed list once (covers all tabs except Corbeille)
      // and the trashed list in parallel so badge counters stay live.
      const [{ data: active }, { data: trashed }] = await Promise.all([
        api.get('/price-requests'),
        api.get('/price-requests?status=trashed').catch(() => ({ data: [] })),
      ]);
      setRequests(active);
      setTrashedRequests(trashed);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const fetchDetailFiles = async (id) => {
    try {
      const { data } = await api.get(`/files/list?entity_type=price_request&entity_id=${id}`);
      setDetailFiles(data);
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    if (!form.client_name.trim()) { toast.error('Client requis'); return; }
    setUploading(true);
    try {
      const payload = {
        client_name: form.client_name,
        description: form.description,
        quantity: form.parts.map(p => `${p.name}: ${p.quantity}`).join(' | '),
        desired_deadline: form.desired_deadline,
        quote_deadline: form.quote_deadline,
        parts: form.parts,
        certifications: form.certifications,
        admin_link: form.admin_link || '',
        price_target: form.price_target ? parseFloat(form.price_target) : null,
        laser_marking_enabled: !!form.laser_marking_enabled,
        laser_marking_text: form.laser_marking_text || '',
        general_tolerance: form.general_tolerance || '',
        surface_finish: form.surface_finish || '',
        heat_treatment: form.heat_treatment || '',
        marking_serialization: form.marking_serialization || '',
        inspection_norm: form.inspection_norm || '',
        packaging: form.packaging || '',
        production_type: form.production_type || '',
        additional_specs: form.additional_specs || '',
        order_already_placed: !!form.order_already_placed,
      };
      let pr;
      if (editingPR) {
        const { data } = await api.put(`/price-requests/${editingPR.id}`, payload);
        pr = data;
      } else {
        payload.attachments = [];
        const { data } = await api.post('/price-requests', payload);
        pr = data;
      }

      // Upload files for each part — applies to BOTH create and edit. Previously
      // this block was guarded by `if (!editingPR)` which silently dropped any
      // files added during an edit (user added a PDF, clicked Save, nothing
      // happened, no preview). The PR id is correctly populated in `pr.id` from
      // either the POST or PUT response, so the upload works in both cases.
      for (const [partIdx, files] of Object.entries(partFiles)) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          await api.post(`/files/upload?entity_type=price_request&entity_id=${pr.id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }

      toast.success(editingPR ? 'Demande modifiée' : 'Demande créée');
      setDialogOpen(false);
      setEditingPR(null);
      setForm(EMPTY_FORM);
      setPartFiles({});
      fetchRequests();
      // After CREATE (not edit): open the broadcast picker so admin can choose
      // who receives the rich quote-ready email.
      if (!editingPR) {
        setBroadcastPR(pr);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setUploading(false); }
  };

  const openEdit = (req) => {
    setEditingPR(req);
    setForm({
      client_name: req.client_name || '',
      description: req.description || '',
      desired_deadline: req.desired_deadline ? String(req.desired_deadline).split('T')[0] : '',
      quote_deadline: req.quote_deadline ? String(req.quote_deadline).split('T')[0] : '',
      certifications: req.certifications || [],
      parts: (req.parts && req.parts.length > 0) ? req.parts : [{ ...EMPTY_PART }],
      admin_link: req.admin_link || '',
      price_target: req.price_target != null ? String(req.price_target) : '',
      laser_marking_enabled: !!req.laser_marking_enabled,
      laser_marking_text: req.laser_marking_text || '',
      general_tolerance: req.general_tolerance || '',
      surface_finish: req.surface_finish || '',
      heat_treatment: req.heat_treatment || '',
      marking_serialization: req.marking_serialization || '',
      inspection_norm: req.inspection_norm || '',
      packaging: req.packaging || '',
      production_type: req.production_type || '',
      additional_specs: req.additional_specs || '',
      order_already_placed: !!req.order_already_placed,
    });
    setPartFiles({});
    setDialogOpen(true);
  };

  const addPart = () => setForm(f => ({ ...f, parts: [...f.parts, { ...EMPTY_PART }] }));
  const removePart = (idx) => {
    setForm(f => ({ ...f, parts: f.parts.filter((_, i) => i !== idx) }));
    setPartFiles(pf => { const n = { ...pf }; delete n[idx]; return n; });
  };
  const updatePart = (idx, field, value) => {
    setForm(f => {
      const parts = [...f.parts];
      parts[idx] = { ...parts[idx], [field]: value };
      if (field === 'material') parts[idx].designation = '';
      return { ...f, parts };
    });
  };

  const handlePartFiles = (idx, files) => {
    const arr = Array.from(files || []);
    setPartFiles(pf => ({ ...pf, [idx]: [...(pf[idx] || []), ...arr] }));
    // Auto-analyse the first PDF/image only if the admin opted in via the
    // "Analyse IA à l'upload" toggle. Otherwise the admin can trigger the
    // analysis manually from the button next to each attached PDF/image.
    if (!aiAutoAnalyze) return;
    const analyzable = arr.find(f => {
      const n = (f.name || '').toLowerCase();
      return ['.pdf', '.png', '.jpg', '.jpeg', '.webp'].some(x => n.endsWith(x));
    });
    if (analyzable) {
      handlePartAiSummary(idx, analyzable, { silent: false });
    }
  };
  const removePartFile = (idx, fileIdx) => {
    setPartFiles(pf => ({ ...pf, [idx]: (pf[idx] || []).filter((_, i) => i !== fileIdx) }));
  };

  const toggleCert = (cert) => {
    setForm(f => ({
      ...f,
      certifications: f.certifications.includes(cert) ? f.certifications.filter(c => c !== cert) : [...f.certifications, cert],
    }));
  };

  // Per-part AI plan summary — uploads a PDF or image and writes a plain-text
  // technical summary into the part's `ai_plan_summary_fr` + `ai_plan_summary_en`
  // fields. Never touches the other fields (no auto-fill). Skips STEP/3D files
  // silently (they can't be read by the vision model).
  // Uses async job pattern: POST returns job_id fast (< 5s), then poll GET
  // every 3s until status is 'done' or 'error'. This avoids Cloudflare/ingress
  // 100s timeout on the multi-pass (30-90s) pipeline.
  const handlePartAiSummary = async (partIdx, file, { silent = false } = {}) => {
    if (!file) return;
    // Only analyze PDFs and images — skip STEP/DWG/etc silently
    const name = (file.name || '').toLowerCase();
    const okExt = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'].some(x => name.endsWith(x));
    if (!okExt) return;
    setAiSummarizing(prev => ({ ...prev, [partIdx]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: kickoff } = await api.post('/price-requests/analyze-plan-summary', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      const jobId = kickoff?.job_id;
      if (!jobId) throw new Error("Job non créé");

      // Poll until done or error (max 5 min)
      const startedAt = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      let result = null;
      let jobError = null;
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        if (Date.now() - startedAt > MAX_MS) {
          jobError = "Analyse trop longue (>5 min), abandon.";
          break;
        }
        try {
          const { data: st } = await api.get(`/price-requests/analyze-plan-summary/${jobId}`, { timeout: 15000 });
          if (st?.status === 'done') { result = st.result; break; }
          if (st?.status === 'error') { jobError = st.error || "Erreur inconnue"; break; }
          // status === 'processing' → keep polling
        } catch (pollErr) {
          // Transient network glitch — keep polling
          if (pollErr?.response?.status === 404) { jobError = "Job introuvable"; break; }
        }
      }

      if (jobError) {
        if (!silent) toast.error(jobError);
        return;
      }
      const fr = (result?.summary_fr || '').trim();
      const en = (result?.summary_en || '').trim();
      if (!fr && !en) { if (!silent) toast.error('L\'IA n\'a rien renvoyé'); return; }
      updatePart(partIdx, 'ai_plan_summary_fr', fr);
      updatePart(partIdx, 'ai_plan_summary_en', en);
      if (!silent) toast.success('✓ Points critiques extraits du plan');
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || err.message || 'Erreur analyse IA');
    } finally {
      setAiSummarizing(prev => ({ ...prev, [partIdx]: false }));
    }
  };

  const handleClose = async (id) => {
    try { await api.put(`/price-requests/${id}`, { status: 'closed' }); toast.success('Demande fermée'); fetchRequests(); } catch { toast.error('Erreur'); }
  };
  const handleReopen = async (id) => {
    try { await api.put(`/price-requests/${id}`, { status: 'open' }); toast.success('Demande rouverte'); fetchRequests(); } catch { toast.error('Erreur'); }
  };
  const handleProposalMade = async (id) => {
    // Open the "pick best-placed subcontractor" dialog. The actual PUT happens
    // when the admin confirms in the dialog (or chooses to skip).
    const target = requests.find(r => r.id === id);
    if (!target) return;
    setProposalPR(target);
  };
  const [clientDeclinePR, setClientDeclinePR] = useState(null);
  const [forwardPR, setForwardPR] = useState(null);
  const [clientDeclineForm, setClientDeclineForm] = useState({ reason: 'prix_trop_haut', detail: '' });
  const [submittingClientDecline, setSubmittingClientDecline] = useState(false);
  const handleSubmitClientDecline = async () => {
    if (!clientDeclinePR) return;
    setSubmittingClientDecline(true);
    try {
      await api.post(`/price-requests/${clientDeclinePR.id}/client-decline`, clientDeclineForm);
      toast.success('Statut mis à jour — sous-traitants notifiés');
      setClientDeclinePR(null);
      setClientDeclineForm({ reason: 'prix_trop_haut', detail: '' });
      fetchRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmittingClientDecline(false);
    }
  };
  const handleRevertClientDecline = async (id) => {
    if (!window.confirm('Annuler le statut "Client à décliné" et remettre la demande en cours ?')) return;
    try {
      await api.delete(`/price-requests/${id}/client-decline`);
      toast.success('Statut annulé');
      fetchRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Déplacer cette demande vers la corbeille ?\nElle sera conservée 30 jours et pourra être restaurée à tout moment.')) return;
    try { await api.delete(`/price-requests/${id}`); toast.success('Déplacée dans la corbeille (30j)'); fetchRequests(); if (detailRequest?.id === id) setDetailRequest(null); } catch { toast.error('Erreur'); }
  };
  const handleRestore = async (id) => {
    try { await api.post(`/price-requests/${id}/restore`); toast.success('Demande restaurée'); fetchRequests(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const handlePermanentDelete = async (id) => {
    if (!window.confirm('SUPPRESSION DÉFINITIVE — cette action est irréversible.\nLa demande, ses réponses et ses pièces jointes seront supprimées immédiatement. Continuer ?')) return;
    try { await api.delete(`/price-requests/${id}/permanent`); toast.success('Supprimée définitivement'); fetchRequests(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // Compute counters + filtered list per active tab
  const hasActiveNegotiation = (r) => (r?.responses || []).some(resp => Array.isArray(resp.negotiations) && resp.negotiations.length > 0);
  const counts = (() => {
    const c = { pending_review: 0, open: 0, negotiation: 0, proposition: 0, won: 0, refused: 0, trash: trashedRequests.length };
    for (const r of requests) {
      if (r.status === 'pending_review') c.pending_review++;
      else if (r.status === 'open') c.open++;
      else if (r.status === 'proposition_faite') c.proposition++;
      else if (r.status === 'closed') c.won++;
      else if (r.status === 'client_declined' || r.status === 'rejected_by_admin') c.refused++;
      if (r.status !== 'trashed' && r.status !== 'closed' && hasActiveNegotiation(r)) c.negotiation++;
    }
    return c;
  })();

  const activeTabConfig = PR_TABS.find(t => t.key === activeTab) || PR_TABS[1];
  const visibleRequests = activeTab === 'trash'
    ? trashedRequests
    : activeTabConfig.custom === 'negotiation'
      ? requests.filter(r => r.status !== 'trashed' && r.status !== 'closed' && hasActiveNegotiation(r))
      : requests.filter(r => activeTabConfig.statuses.includes(r.status));

  const openDetail = (req) => { setDetailRequest(req); fetchDetailFiles(req.id); };

  // Chat handlers (admin)
  const openChat = async (req) => {
    setChatRequest(req);
    setActiveThreadSubId(null);
    setChatMessages([]);
    setNewChatText('');
    try {
      const { data } = await api.get(`/price-requests/${req.id}/threads`);
      setThreads(data);
      if (data.length === 1) {
        await selectThread(req.id, data[0].subcontractor_id);
      }
    } catch { toast.error('Erreur'); }
  };

  const selectThread = async (prId, subId) => {
    setActiveThreadSubId(subId);
    try {
      const { data } = await api.get(`/price-requests/${prId}/messages?subcontractor_id=${subId}`);
      setChatMessages(data);
      // Mark thread as read
      await api.post(`/price-requests/${prId}/messages/read?subcontractor_id=${subId}`);
      // Refresh threads to clear the unread badge
      const { data: th } = await api.get(`/price-requests/${prId}/threads`);
      setThreads(th);
      fetchRequests();
    } catch { setChatMessages([]); }
  };

  const sendChatMessage = async () => {
    if ((!newChatText.trim() && chatPendingFiles.length === 0) || !activeThreadSubId) return;
    try {
      const thread = threads.find(t => t.subcontractor_id === activeThreadSubId);
      const { data } = await api.post(`/price-requests/${chatRequest.id}/messages`, {
        text: newChatText,
        subcontractor_id: activeThreadSubId,
        subcontractor_name: thread?.subcontractor_name || '',
        attachments: chatPendingFiles.map(f => f.id),
      });
      // Re-fetch to get attachments_meta enriched
      const { data: msgs } = await api.get(`/price-requests/${chatRequest.id}/messages?subcontractor_id=${activeThreadSubId}`);
      setChatMessages(msgs);
      setNewChatText('');
      setChatPendingFiles([]);
      const { data: th } = await api.get(`/price-requests/${chatRequest.id}/threads`);
      setThreads(th);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const uploadChatFiles = async (fileList) => {
    if (!fileList || fileList.length === 0 || !chatRequest) return;
    setChatUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/files/upload?entity_type=pr_message&entity_id=${chatRequest.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setChatPendingFiles(prev => [...prev, data]);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { setChatUploading(false); }
  };

  const removePendingFile = async (fileId) => {
    try { await api.delete(`/files/${fileId}`); } catch { /* ignore */ }
    setChatPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const downloadAttachment = async (file) => {
    try {
      const response = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Téléchargement échoué'); }
  };

  const fmtCur = (val, cur) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: cur || 'USD' }).format(val || 0);

  return (
    <div className="space-y-6" data-testid="price-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Demandes de prix</h1>
          <p className="text-sm text-zinc-500 mt-1">{requests.length + trashedRequests.length} demande(s){trashedRequests.length > 0 ? ` · ${trashedRequests.length} dans la corbeille` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setMergeMode(m => !m); setMergeSelected(new Set()); }}
            className={`rounded-sm text-xs h-9 border-zinc-700 ${mergeMode ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'}`}
            data-testid="merge-toggle-btn"
          >
            <Box className="w-3.5 h-3.5 mr-1.5" />
            {mergeMode ? 'Annuler fusion' : 'Fusionner'}
          </Button>
          <Button data-testid="new-price-request-btn" onClick={() => { setEditingPR(null); setForm({ client_name: '', description: '', desired_deadline: '', quote_deadline: '', certifications: [], parts: [{ ...EMPTY_PART }], admin_link: '', price_target: '', laser_marking_enabled: false, laser_marking_text: '' }); setPartFiles({}); setDialogOpen(true); }} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
          </Button>
        </div>
      </div>

      <Select value={activeTab} onValueChange={setActiveTab}>
        <SelectTrigger className="w-44 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm md:hidden" data-testid="pr-tab-select-mobile"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          {PR_TABS.map(t => (
            <SelectItem key={t.key} value={t.key}>{t.label} ({counts[t.key]})</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Tab bar (md+) */}
      <div className="hidden md:flex items-center gap-1 border-b border-zinc-800 -mb-px overflow-x-auto" data-testid="pr-tabs">
        {PR_TABS.map(t => {
          const accent = TAB_ACCENTS[t.accent] || TAB_ACCENTS.zinc;
          const isActive = activeTab === t.key;
          const count = counts[t.key] || 0;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`pr-tab-${t.key}`}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? `${accent.active} border-current`
                  : 'text-zinc-500 hover:text-zinc-200 border-transparent'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{t.label}</span>
              {count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm text-[11px] font-semibold ${isActive ? accent.badge : 'bg-zinc-800 text-zinc-400'}`} data-testid={`pr-tab-count-${t.key}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Pending review banner — only shows when not already on the pending tab */}
      {counts.pending_review > 0 && activeTab !== 'pending_review' && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-sm" data-testid="pending-review-banner">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-amber-100">
                {counts.pending_review} demande{counts.pending_review > 1 ? 's' : ''} client à valider
              </div>
              <div className="text-xs text-amber-200/80">
                Vérifie l'absence de coordonnées personnelles avant publication aux sous-traitants.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setActiveTab('pending_review')}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-8"
            data-testid="banner-view-pending"
          >
            Voir les soumissions
          </Button>
        </div>
      )}

      {/* Merge action bar — sticky above list when merge mode is on */}
      {mergeMode && (
        <div className="sticky top-2 z-10 flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/10 border border-amber-500/30 rounded-sm text-sm" data-testid="merge-action-bar">
          <div className="flex items-center gap-2 text-amber-200">
            <Box className="w-4 h-4" />
            <span>
              {mergeSelected.size === 0
                ? 'Sélectionne 2 demandes ou plus (les statuts fermé/corbeille sont exclus)'
                : `${mergeSelected.size} sélectionnée${mergeSelected.size > 1 ? 's' : ''} · La demande "primaire" recevra les pièces et offres des autres, qui seront ensuite supprimées définitivement.`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {mergeSelected.size > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setMergeSelected(new Set())} className="text-zinc-400 hover:text-zinc-100 h-8" data-testid="merge-clear-btn">
                Effacer
              </Button>
            )}
            <Button
              size="sm"
              disabled={mergeSelected.size < 2}
              onClick={() => { setMergePrimaryId(Array.from(mergeSelected)[0] || ''); setMergeDialog(true); }}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="merge-open-dialog-btn"
            >
              <Box className="w-3.5 h-3.5 mr-1.5" /> Fusionner {mergeSelected.size} demandes
            </Button>
          </div>
        </div>
      )}

      {/* Trash header — only on Corbeille tab */}
      {activeTab === 'trash' && (
        <div className="flex items-center gap-3 px-4 py-3 bg-zinc-900/50 border border-zinc-800 rounded-sm text-xs text-zinc-400" data-testid="trash-header">
          <Trash2 className="w-4 h-4 text-zinc-500 shrink-0" />
          <span>
            Les demandes restent dans la corbeille pendant <strong className="text-zinc-200">30 jours</strong>, puis sont supprimées automatiquement. Tu peux les restaurer ou les supprimer définitivement à tout moment.
          </span>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-zinc-900 border border-zinc-800 rounded-sm nexus-shimmer" />) :
         visibleRequests.length === 0 ? (
            <div className="text-center py-16 text-zinc-500" data-testid="pr-empty-state">
              {activeTab === 'trash' ? 'La corbeille est vide' : 'Aucune demande dans cet onglet'}
            </div>
         ) :
         visibleRequests.map(req => {
          const meta = req.attachments_meta || [];
          // Find a previewable attachment (PDF or image) — first match wins
          const previewable = meta.find(a => {
            const ct = (a?.content_type || '').toLowerCase();
            const fn = (a?.original_filename || '').toLowerCase();
            return ct.includes('pdf') || fn.endsWith('.pdf')
              || ct.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(fn);
          });
          // Fallback: detect a 3D CAD attachment (.step, .stp, .iges, .igs, .stl, .obj, .x_t, .x_b, .dxf, .dwg, .sldprt, .ipt, .prt)
          const cadFile = !previewable ? meta.find(a => {
            const fn = (a?.original_filename || '').toLowerCase();
            return /\.(step|stp|iges|igs|stl|obj|x_t|x_b|dxf|dwg|sldprt|sldasm|ipt|prt|catpart|catproduct)$/i.test(fn);
          }) : null;
          const previewToken = localStorage.getItem('pp_access_token') || '';
          const previewUrl = previewable
            ? `${process.env.REACT_APP_BACKEND_URL}/api/files/thumbnail/${previewable.id}?width=200&auth=${encodeURIComponent(previewToken)}`
            : null;
          const cadExt = cadFile ? (cadFile.original_filename.split('.').pop() || '').toUpperCase() : null;
          const isPending = req.status === 'pending_review';
          const isRejected = req.status === 'rejected_by_admin';
          return (
          <Card
            key={req.id}
            data-focus-id={req.id}
            className={`bg-zinc-900 border-zinc-800 rounded-sm nexus-card ${isPending ? 'border-l-4 border-l-amber-500' : ''} ${isRejected ? 'border-l-4 border-l-rose-500 opacity-70' : ''} ${mergeSelected.has(req.id) ? 'ring-2 ring-amber-500/60' : ''}`}
            data-testid={`pr-card-${req.id}`}
          >
            <CardContent className="p-4">
              {mergeMode && req.status !== 'trashed' && req.status !== 'closed' && (
                <div className="mb-3 flex items-center gap-2 text-xs bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                  <input
                    type="checkbox"
                    checked={mergeSelected.has(req.id)}
                    onChange={(e) => {
                      const next = new Set(mergeSelected);
                      if (e.target.checked) next.add(req.id); else next.delete(req.id);
                      setMergeSelected(next);
                    }}
                    className="w-4 h-4 accent-amber-500"
                    data-testid={`merge-checkbox-${req.id}`}
                  />
                  <span className="text-zinc-400">{mergeSelected.has(req.id) ? 'Sélectionnée pour fusion' : 'Cocher pour fusionner'}</span>
                </div>
              )}
              {(req.merged_from || []).length > 0 && (
                <div className="mb-3 flex items-start gap-2 text-[11px] bg-amber-500/5 border border-amber-500/20 text-amber-200 rounded-sm px-3 py-1.5" title={req.merged_from.map(m => `${m.reference || m.id?.slice(0,8)} · ${m.client_name || ''}`).join('\n')} data-testid={`merged-from-${req.id}`}>
                  <Box className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>Fusionnée à partir de {req.merged_from.length} demande{req.merged_from.length > 1 ? 's' : ''} : {req.merged_from.map(m => m.reference || (m.id || '').slice(0, 8)).join(', ')}</span>
                </div>
              )}
              {isPending && (
                <div className="mb-3 flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-sm px-3 py-2">
                  <ShieldAlert className="w-4 h-4 shrink-0 text-amber-400" />
                  <div className="flex-1">
                    <strong>Soumission client — à valider.</strong>{' '}
                    Vérifie le contenu (coordonnées, infos sensibles) avant de publier vers les sous-traitants.
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm(`Publier cette demande aux sous-traitants ?`)) return;
                      try {
                        await api.post(`/price-requests/${req.id}/approve`, {});
                        toast.success('Demande publiée aux sous-traitants');
                        fetchRequests();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white h-7"
                    data-testid={`approve-pr-${req.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(req)}
                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 h-7"
                    data-testid={`edit-pr-${req.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const reason = window.prompt('Motif du rejet (visible par le client) :', 'Merci de préciser votre demande');
                      if (reason === null) return;
                      try {
                        await api.post(`/price-requests/${req.id}/reject-pending`, { reason });
                        toast.success('Demande rejetée et client notifié');
                        fetchRequests();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }}
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-7"
                    data-testid={`reject-pr-${req.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                  </Button>
                </div>
              )}
              <div className="flex items-start gap-3">
                {(previewUrl || cadFile) && (
                  <div className="relative shrink-0">
                    {previewUrl ? (
                      <button
                        type="button"
                        onClick={() => openDetail(req)}
                        className="block w-20 h-24 bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden hover:border-amber-500/60 transition-colors group"
                        data-testid={`pr-thumbnail-${req.id}`}
                        title={previewable.original_filename}
                      >
                        <img
                          src={previewUrl}
                          alt="plan"
                          className="w-full h-full object-cover bg-white group-hover:scale-[1.04] transition-transform"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openDetail(req)}
                        className="block w-20 h-24 bg-gradient-to-br from-blue-500/5 via-zinc-950 to-zinc-950 border border-zinc-800 rounded-sm overflow-hidden hover:border-blue-500/60 transition-colors group flex flex-col items-center justify-center"
                        data-testid={`pr-thumbnail-cad-${req.id}`}
                        title={cadFile.original_filename}
                      >
                        <Box className="w-6 h-6 text-blue-400/80 group-hover:text-blue-400 transition-colors" />
                        <div className="text-[9px] font-bold uppercase tracking-wider text-blue-400 mt-1">{cadExt}</div>
                        <div className="text-[8px] text-zinc-500 mt-0.5">3D / CAO</div>
                      </button>
                    )}
                    {meta.length > 1 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 bg-zinc-800 border border-zinc-700 text-zinc-200 text-[10px] font-medium px-1.5 py-0.5 rounded-full shadow-sm"
                        title={`${meta.length} fichiers joints`}
                      >
                        +{meta.length - 1}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-sm font-semibold text-zinc-100">{req.client_name}</h3>
                    {(() => {
                      const map = {
                        pending_review: { label: '🔍 À valider', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
                        open: { label: 'Ouverte', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                        proposition_faite: { label: 'Proposition faite', cls: 'bg-violet-500/10 text-violet-300 border-violet-500/30' },
                        client_declined: { label: 'Client à décliné', cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
                        rejected_by_admin: { label: 'Rejetée', cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30' },
                        closed: { label: 'Fermée', cls: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
                        trashed: { label: '🗑 Corbeille', cls: 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40' },
                      };
                      const s = map[req.status] || map.closed;
                      return <span className={`text-xs px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span>;
                    })()}
                    <RetentionBadge req={req} />
                    <span className="text-xs text-zinc-500">{req.response_count} réponse(s)</span>
                    {req.order_already_placed && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-rose-500/15 text-rose-300 border border-rose-500/40 font-medium" data-testid={`admin-order-placed-${req.id}`} title="Une bannière rouge est affichée aux sous-traitants pour les inciter à se positionner vite">
                        🔴 Commande passée
                      </span>
                    )}
                  </div>
                  {req.status === 'client_declined' && req.client_declined_reason_label && (
                    <div className="mb-2 bg-rose-500/5 border border-rose-500/20 rounded-sm px-2 py-1 text-[11px] text-rose-300" data-testid={`client-decline-banner-${req.id}`}>
                      <strong>Client à décliner :</strong> {req.client_declined_reason_label}
                      {req.client_declined_detail && <> — <span className="text-rose-200/80">{req.client_declined_detail}</span></>}
                    </div>
                  )}
                  <p className="text-sm text-zinc-400 mb-1 line-clamp-3 whitespace-pre-wrap">{req.description}</p>
                  {req.parts?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {req.parts.map((p, i) => (
                        <span key={i} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm">
                          {p.name || `Pièce ${i + 1}`}{p.material ? ` - ${MATERIALS[p.material]?.label || p.material}` : ''}{p.quantity ? ` x${p.quantity}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Aggregated special points across all parts — quick visual on the card */}
                  {(() => {
                    const allSpecial = new Set();
                    (req.parts || []).forEach(p => (p.special_points || []).forEach(k => allSpecial.add(k)));
                    if (allSpecial.size === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1 mb-1" data-testid={`pr-special-points-${req.id}`}>
                        {Array.from(allSpecial).slice(0, 5).map(k => {
                          const meta = getSpecialPoint(k, 'fr');
                          if (!meta) return null;
                          return <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${meta.cls.on}`} title={meta.hint}>⚠ {meta.label}</span>;
                        })}
                        {allSpecial.size > 5 && <span className="text-[10px] px-1.5 py-0.5 text-zinc-500">+{allSpecial.size - 5}</span>}
                      </div>
                    );
                  })()}
                  <div className="flex flex-wrap gap-2 items-center text-xs text-zinc-500">
                    {req.desired_deadline && <span>Délai livraison: {formatDate(req.desired_deadline)}</span>}
                    {req.quote_deadline && <QuoteDeadlineBadge deadline={req.quote_deadline} lang="fr" dense />}
                    <span>Créée le {formatDate(req.created_at)}</span>
                  </div>
                </div>
              </div>
              {/* Best proposal snapshot — shows the lowest-total quote without needing to open the details */}
              {(() => {
                const responses = req.responses || [];
                if (responses.length === 0) return null;
                const totalOf = (r) => (r?.price_ttc || 0) + (r?.transport_cost || 0) + (r?.other_fees || 0);
                const best = [...responses].sort((a, b) => totalOf(a) - totalOf(b))[0];
                if (!best || !totalOf(best)) return null;
                const cur = best.currency || 'EUR';
                const formatted = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: cur }).format(totalOf(best));
                return (
                  <div className="mt-2 flex items-center justify-between gap-2 bg-amber-500/5 border border-amber-500/20 rounded-sm px-3 py-1.5" data-testid={`best-proposal-${req.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Trophy className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <span className="text-[11px] text-zinc-400">Meilleure offre</span>
                      <span className="text-[11px] text-amber-300 font-medium truncate">{best.subcontractor_name}</span>
                      <span className="text-[10px] text-zinc-600">· {responses.length} offre{responses.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-sm font-bold text-amber-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatted}</span>
                      {best.deadline && <span className="text-[10px] text-zinc-500 ml-1">{best.deadline}</span>}
                    </div>
                  </div>
                );
              })()}
              {/* Action row — wraps on narrow viewports, doesn't squeeze content.
                  TRASH tab: only Voir + Restaurer + Supprimer définitivement + countdown.
                  Other tabs: regular workflow actions. */}
              {req.status === 'trashed' ? (
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-zinc-800/40">
                  <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5" data-testid={`trash-countdown-${req.id}`}>
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500/80" />
                    {(() => {
                      if (!req.trashed_at) return 'Supprimée bientôt';
                      const days = Math.max(0, 30 - Math.floor((Date.now() - new Date(req.trashed_at).getTime()) / 86400000));
                      const by = req.trashed_by_name ? ` par ${req.trashed_by_name}` : '';
                      return days > 0
                        ? `Suppression auto dans ${days} jour${days > 1 ? 's' : ''}${by}`
                        : `Suppression imminente${by}`;
                    })()}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => openDetail(req)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid={`view-request-${req.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleRestore(req.id)} className="border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 rounded-sm text-xs h-8" data-testid={`restore-request-${req.id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurer
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handlePermanentDelete(req.id)} className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid={`permanent-delete-${req.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer définitivement
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="flex flex-wrap items-center justify-end gap-1 mt-3 pt-3 border-t border-zinc-800/40">
                  <Button size="sm" variant="outline" onClick={() => openDetail(req)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid={`view-request-${req.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openChat(req)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8 relative" data-testid={`chat-request-${req.id}`}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Messages
                    {(req.unread_count || 0) > 0 ? (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold bg-rose-500 text-zinc-50 animate-pulse" data-testid={`unread-badge-${req.id}`}>{req.unread_count}</span>
                    ) : (req.messages_count || 0) > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold bg-amber-500 text-zinc-950">{req.messages_count}</span>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(req)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid={`edit-request-${req.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setForwardPR(req)} className="border-blue-500/30 bg-blue-500/5 text-blue-300 hover:bg-blue-500/10 rounded-sm text-xs h-8" data-testid={`forward-request-${req.id}`}>
                    <Forward className="w-3.5 h-3.5 mr-1" /> Transférer
                  </Button>
                  {req.status !== 'proposition_faite' && req.status !== 'closed' && req.status !== 'client_declined' && (
                    <Button size="sm" variant="outline" onClick={() => handleProposalMade(req.id)} className="border-violet-500/30 bg-violet-500/5 text-violet-300 hover:bg-violet-500/10 rounded-sm text-xs h-8" data-testid={`mark-proposal-${req.id}`}>
                      Proposition faite
                    </Button>
                  )}
                  {req.status !== 'closed' && req.status !== 'client_declined' && (
                    <Button size="sm" variant="outline" onClick={() => setClientDeclinePR(req)} className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid={`client-decline-${req.id}`}>
                      Client à décliner
                    </Button>
                  )}
                  {req.status === 'client_declined' && (
                    <Button size="sm" variant="outline" onClick={() => handleRevertClientDecline(req.id)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid={`revert-client-decline-${req.id}`}>
                      Annuler décline
                    </Button>
                  )}
                  {req.status === 'open' ? (
                    <Button size="sm" variant="outline" onClick={() => handleClose(req.id)} className="border-zinc-700 text-amber-400 hover:bg-zinc-800 rounded-sm text-xs h-8">Fermer</Button>
                  ) : req.status !== 'client_declined' ? (
                    <Button size="sm" variant="outline" onClick={() => handleReopen(req.id)} className="border-zinc-700 text-emerald-400 hover:bg-zinc-800 rounded-sm text-xs h-8">Rouvrir</Button>
                  ) : null}
                  <button onClick={() => handleDelete(req.id)} className="p-1.5 text-zinc-500 hover:text-rose-400" title="Déplacer dans la corbeille (30j)" data-testid={`trash-request-${req.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* CREATE DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="price-request-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{editingPR ? 'Modifier la demande' : 'Nouvelle demande de prix'}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Renseignez les détails et ajoutez vos fichiers STEP / plans.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* General info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Client *</Label>
                <Input data-testid="pr-client" value={form.client_name} onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Délai souhaité <span className="text-zinc-600 text-xs">(livraison)</span></Label>
                <Input type="date" value={form.desired_deadline} onChange={(e) => setForm(f => ({ ...f, desired_deadline: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
            </div>

            {/* Quote deadline — visible to subcontractors as a countdown */}
            <div className="space-y-2 bg-blue-500/5 border border-blue-500/20 rounded-sm p-3" data-testid="pr-quote-deadline-section">
              <Label className="text-blue-300 text-sm font-medium flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Délai de réponse souhaité (sous-traitant)
                <span className="text-[10px] uppercase tracking-wider bg-blue-500/10 text-blue-300 border border-blue-500/30 px-1.5 py-0.5 rounded-sm">Visible des sous-traitants</span>
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="date"
                  value={form.quote_deadline}
                  onChange={(e) => setForm(f => ({ ...f, quote_deadline: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm flex-1 min-w-[180px]"
                  data-testid="pr-quote-deadline"
                />
                <div className="flex items-center gap-1" data-testid="pr-quote-deadline-presets">
                  {[
                    { d: 1,  label: '24h' },
                    { d: 2,  label: '48h' },
                    { d: 7,  label: '7j'  },
                    { d: 14, label: '14j' },
                  ].map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        const dt = new Date(); dt.setDate(dt.getDate() + p.d);
                        setForm(f => ({ ...f, quote_deadline: dt.toISOString().split('T')[0] }));
                      }}
                      className="text-[11px] px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:bg-blue-500/10 hover:text-blue-300 hover:border-blue-500/30 transition-colors"
                      data-testid={`pr-quote-preset-${p.label}`}
                    >
                      +{p.label}
                    </button>
                  ))}
                  {form.quote_deadline && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, quote_deadline: '' }))}
                      className="text-[11px] px-2 py-1 rounded-sm text-zinc-500 hover:text-rose-400"
                      data-testid="pr-quote-deadline-clear"
                    >
                      Effacer
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-zinc-600">Date avant laquelle le sous-traitant doit envoyer son offre. Un compte à rebours s'affiche dans son portail.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Description générale du projet</Label>
              <Textarea data-testid="pr-description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[60px]" placeholder="Description du besoin, contexte..." />
            </div>

            {/* Admin-only reference link (hidden from subcontractors) */}
            <div className="space-y-2 bg-amber-500/5 border border-amber-500/20 rounded-sm p-3">
              <Label className="text-amber-400 text-sm font-medium flex items-center gap-1.5">
                Lien interne (admin uniquement)
                <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-sm">Privé</span>
              </Label>
              <Input data-testid="pr-admin-link" value={form.admin_link} onChange={(e) => setForm(f => ({ ...f, admin_link: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Ex: https://drive.google.com/... ou note interne" />
              <p className="text-[11px] text-zinc-600">Visible uniquement par l'admin ProdLink. Jamais transmis aux sous-traitants.</p>
            </div>

            {/* Target price — visible to subcontractors too */}
            <div className="space-y-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3">
              <Label className="text-emerald-400 text-sm font-medium flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" /> Objectif de prix
                <span className="text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded-sm">Visible des sous-traitants</span>
              </Label>
              <Input type="number" step="0.01" data-testid="pr-price-target" value={form.price_target} onChange={(e) => setForm(f => ({ ...f, price_target: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Ex: 1500.00 (en €)" />
              <p className="text-[11px] text-zinc-600">Affiché aux sous-traitants pour les aider à se positionner.</p>
            </div>

            {/* Quality specs block — dropdowns for standard industrial choices.
                Free-text hints live in "Autres spécifications" below. Every
                dropdown starts on "Aucun / non spécifié" so admin can leave
                it blank without typing anything. */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-3">
              <Label className="text-zinc-300 text-sm font-medium">Spécifications qualité (limite les erreurs)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Tolérance générale</Label>
                  <Select value={form.general_tolerance || 'none'} onValueChange={(v) => setForm(f => ({ ...f, general_tolerance: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-general-tolerance" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Aucune / non spécifiée</SelectItem>
                      <SelectItem value="ISO 2768-mK">ISO 2768-mK (moyen fin)</SelectItem>
                      <SelectItem value="ISO 2768-fH">ISO 2768-fH (fin serré)</SelectItem>
                      <SelectItem value="ISO 2768-mH">ISO 2768-mH (moyen serré)</SelectItem>
                      <SelectItem value="ISO 2768-cL">ISO 2768-cL (grossier)</SelectItem>
                      <SelectItem value="Selon plan">Selon plan (tolérances cotées)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">État de surface (Ra)</Label>
                  <Select value={form.surface_finish || 'none'} onValueChange={(v) => setForm(f => ({ ...f, surface_finish: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-surface-finish" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Aucun / standard usinage</SelectItem>
                      <SelectItem value="Ra 0.4 µm">Ra 0.4 µm (miroir)</SelectItem>
                      <SelectItem value="Ra 0.8 µm">Ra 0.8 µm (poli fin)</SelectItem>
                      <SelectItem value="Ra 1.6 µm">Ra 1.6 µm (fraisage fin)</SelectItem>
                      <SelectItem value="Ra 3.2 µm">Ra 3.2 µm (fraisage standard)</SelectItem>
                      <SelectItem value="Ra 6.3 µm">Ra 6.3 µm (ébauche)</SelectItem>
                      <SelectItem value="Selon plan">Selon plan (indiqué)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Traitement thermique</Label>
                  <Select value={form.heat_treatment || 'none'} onValueChange={(v) => setForm(f => ({ ...f, heat_treatment: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-heat-treatment" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Aucun</SelectItem>
                      <SelectItem value="Trempe + revenu">Trempe + revenu</SelectItem>
                      <SelectItem value="Trempe HRC 45-50">Trempe HRC 45-50</SelectItem>
                      <SelectItem value="Trempe HRC 50-55">Trempe HRC 50-55</SelectItem>
                      <SelectItem value="Trempe HRC 55-60">Trempe HRC 55-60</SelectItem>
                      <SelectItem value="Trempe HRC 60-63">Trempe HRC 60-63</SelectItem>
                      <SelectItem value="Cémentation">Cémentation</SelectItem>
                      <SelectItem value="Nitruration">Nitruration</SelectItem>
                      <SelectItem value="Recuit">Recuit</SelectItem>
                      <SelectItem value="Recuit de détente">Recuit de détente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Marquage / N° série</Label>
                  <Select value={form.marking_serialization || 'none'} onValueChange={(v) => setForm(f => ({ ...f, marking_serialization: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-marking-serial" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Aucun</SelectItem>
                      <SelectItem value="Gravure laser N° série">Gravure laser N° série</SelectItem>
                      <SelectItem value="Gravure laser N° lot">Gravure laser N° lot</SelectItem>
                      <SelectItem value="Gravure laser logo">Gravure laser logo</SelectItem>
                      <SelectItem value="Gravure mécanique">Gravure mécanique</SelectItem>
                      <SelectItem value="Marquage encre">Marquage encre</SelectItem>
                      <SelectItem value="Étiquette autocollante">Étiquette autocollante</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Norme d'inspection</Label>
                  <Select value={form.inspection_norm || 'none'} onValueChange={(v) => setForm(f => ({ ...f, inspection_norm: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-inspection-norm" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Standard fournisseur</SelectItem>
                      <SelectItem value="Contrôle dimensionnel 100%">Contrôle dimensionnel 100 %</SelectItem>
                      <SelectItem value="Contrôle par échantillonnage AQL 2.5">Échantillonnage AQL 2.5</SelectItem>
                      <SelectItem value="FAI (First Article Inspection)">FAI (First Article Inspection)</SelectItem>
                      <SelectItem value="PPAP niveau 3">PPAP niveau 3</SelectItem>
                      <SelectItem value="ISIR (Initial Sample Inspection)">ISIR</SelectItem>
                      <SelectItem value="Rapport de contrôle 3.1">Rapport de contrôle 3.1</SelectItem>
                      <SelectItem value="Rapport de contrôle 3.2">Rapport de contrôle 3.2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Conditionnement</Label>
                  <Select value={form.packaging || 'none'} onValueChange={(v) => setForm(f => ({ ...f, packaging: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-packaging" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Standard fournisseur</SelectItem>
                      <SelectItem value="Emballage unitaire mousse">Emballage unitaire mousse</SelectItem>
                      <SelectItem value="Emballage unitaire film bulles">Emballage unitaire film bulles</SelectItem>
                      <SelectItem value="Emballage sachet plastique">Emballage sachet plastique</SelectItem>
                      <SelectItem value="Carton + calage">Carton + calage</SelectItem>
                      <SelectItem value="Caisse bois">Caisse bois</SelectItem>
                      <SelectItem value="Palette Europe">Palette Europe</SelectItem>
                      <SelectItem value="Vrac">Vrac</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-500 text-xs">Type de production</Label>
                  <Select value={form.production_type || 'none'} onValueChange={(v) => setForm(f => ({ ...f, production_type: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="pr-production-type" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">-</SelectItem>
                      <SelectItem value="prototype">Prototype</SelectItem>
                      <SelectItem value="preserie">Pré-série</SelectItem>
                      <SelectItem value="serie">Série</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-zinc-500 text-xs">Autres spécifications</Label>
                  <Input data-testid="pr-additional-specs" value={form.additional_specs} onChange={(e) => setForm(f => ({ ...f, additional_specs: e.target.value }))} placeholder="Toute info utile complémentaire" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" />
                </div>
              </div>
              {/* Urgent flag: ProdLink already has the order from the client */}
              <label className="flex items-start gap-2 mt-3 bg-rose-500/5 border border-rose-500/20 rounded-sm p-2.5 cursor-pointer hover:bg-rose-500/10 transition-colors" data-testid="pr-order-already-placed-row">
                <input
                  type="checkbox"
                  data-testid="pr-order-already-placed"
                  checked={!!form.order_already_placed}
                  onChange={(e) => setForm(f => ({ ...f, order_already_placed: e.target.checked }))}
                  className="mt-0.5 accent-rose-500"
                />
                <div className="text-xs">
                  <span className="text-rose-300 font-semibold block">Commande déjà passée chez ProdLink</span>
                  <span className="text-zinc-500">Une bannière rouge s'affichera aux sous-traitants pour les inciter à se positionner rapidement.</span>
                </div>
              </label>
            </div>

            {/* Laser marking */}
            <div className="space-y-2 bg-zinc-950 border border-zinc-800 rounded-sm p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="pr-laser-enabled" checked={form.laser_marking_enabled} onChange={(e) => setForm(f => ({ ...f, laser_marking_enabled: e.target.checked }))}
                  className="w-4 h-4 rounded-sm accent-amber-500" />
                <span className="text-zinc-200 text-sm font-medium">Marquage au laser</span>
              </label>
              {form.laser_marking_enabled && (
                <div className="pl-6 space-y-1">
                  <Label className="text-zinc-400 text-xs">Texte à marquer</Label>
                  <Textarea data-testid="pr-laser-text" value={form.laser_marking_text} onChange={(e) => setForm(f => ({ ...f, laser_marking_text: e.target.value }))}
                    placeholder="Ex: Lot N° 2026-001, logo, référence client..."
                    className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm min-h-[60px]" />
                </div>
              )}
            </div>

            {/* Certifications */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Certifications requises</Label>
              <div className="flex flex-wrap gap-2">
                {CERTIFICATIONS.map(cert => (
                  <button key={cert.fr} type="button" onClick={() => toggleCert(cert.fr)}
                    className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${form.certifications.includes(cert.fr) ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:border-zinc-700'}`}>
                    {cert.fr}
                  </button>
                ))}
              </div>
            </div>

            {/* Parts */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-zinc-300 text-sm font-medium">Pièces ({form.parts.length})</Label>
                <Button type="button" size="sm" variant="outline" onClick={addPart} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-7">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter une pièce
                </Button>
              </div>

              <div className="space-y-4">
                {form.parts.map((part, idx) => (
                  <div key={idx} className="bg-zinc-950 border border-zinc-800 rounded-sm p-4" data-testid={`part-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-zinc-200">Pièce {idx + 1}</span>
                      </div>
                      {form.parts.length > 1 && (
                        <button type="button" onClick={() => removePart(idx)} className="text-zinc-600 hover:text-rose-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Nom de la pièce</Label>
                        <Input value={part.name} onChange={(e) => updatePart(idx, 'name', e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" placeholder="Ex: Support moteur" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Matériau</Label>
                        <Select value={part.material} onValueChange={(v) => updatePart(idx, 'material', v === 'none' ? '' : v)}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                            <SelectItem value="none">-</SelectItem>
                            {Object.entries(MATERIALS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Désignation</Label>
                        <Select value={part.designation} onValueChange={(v) => updatePart(idx, 'designation', v === 'none' ? '' : v)} disabled={!part.material}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder={part.material ? "Choisir" : "Matériau d'abord"} /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                            <SelectItem value="none">-</SelectItem>
                            {part.material && MATERIALS[part.material]?.designations.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-3">
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Quantité</Label>
                        <Input value={part.quantity} onChange={(e) => updatePart(idx, 'quantity', e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" placeholder="Ex: 50" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Traitement de surface</Label>
                        <Select value={part.surface_treatment} onValueChange={(v) => updatePart(idx, 'surface_treatment', v)}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                            {SURFACE_TREATMENTS.map(t => <SelectItem key={t.fr} value={t.fr}>{t.fr}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Tolérance</Label>
                        <Select value={part.tolerance} onValueChange={(v) => updatePart(idx, 'tolerance', v)}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {TOLERANCE_CLASSES.map(t => <SelectItem key={t.fr} value={t.fr}>{t.fr}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-zinc-500 text-xs">Procédé</Label>
                        <Select value={part.process} onValueChange={(v) => updatePart(idx, 'process', v === 'none' ? '' : v)}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                            <SelectItem value="none">-</SelectItem>
                            {MANUFACTURING_PROCESSES.map(p => <SelectItem key={p.fr} value={p.fr}>{p.fr}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* RAL / Color field — shown only for color-related surface treatments */}
                    {COLOR_TREATMENTS.includes(part.surface_treatment) && (
                      <div className="space-y-1.5 mb-3">
                        <Label className="text-zinc-500 text-xs">RAL / Couleur <span className="text-amber-500">*</span></Label>
                        <Input
                          data-testid={`part-ral-${idx}`}
                          value={part.color_ral || ''}
                          onChange={(e) => updatePart(idx, 'color_ral', e.target.value)}
                          placeholder="Ex: RAL 9005 (noir profond), RAL 3020, ou description couleur"
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" />
                      </div>
                    )}

                    {/* Points particuliers de fabrication — attention points for the sub */}
                    <div className="space-y-1.5 mb-3">
                      <Label className="text-zinc-500 text-xs">Points particuliers de fabrication</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid={`part-special-points-${idx}`}>
                        {PART_SPECIAL_POINTS.map(sp => {
                          const isOn = (part.special_points || []).includes(sp.key);
                          const cls = SP_CHIP_CLS[sp.color] || SP_CHIP_CLS.zinc;
                          return (
                            <button
                              key={sp.key}
                              type="button"
                              onClick={() => {
                                const cur = new Set(part.special_points || []);
                                if (isOn) cur.delete(sp.key); else cur.add(sp.key);
                                updatePart(idx, 'special_points', Array.from(cur));
                              }}
                              title={sp.hint_fr}
                              className={`text-[11px] px-2 py-0.5 rounded-sm border transition-colors ${isOn ? cls.on : cls.off}`}
                              data-testid={`part-sp-${idx}-${sp.key}`}
                            >
                              {isOn ? '✓ ' : '☐ '}{sp.fr}
                            </button>
                          );
                        })}
                      </div>
                      {(part.special_points || []).length > 0 && (
                        <p className="text-[10px] text-zinc-500">
                          {(part.special_points || []).length} point{(part.special_points || []).length > 1 ? 's' : ''} sélectionné{(part.special_points || []).length > 1 ? 's' : ''} — bien visibles pour le sous-traitant
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5 mb-3">
                      <Label className="text-zinc-500 text-xs">Notes spécifiques à cette pièce</Label>
                      <Input value={part.notes} onChange={(e) => updatePart(idx, 'notes', e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" placeholder="Dimensions, remarques..." />
                    </div>

                    {/* AI plan technical summary — auto-generated from the file
                        upload above (PDF/image). Bilingual FR + EN, editable. */}
                    {(part.ai_plan_summary_fr || part.ai_plan_summary_en || aiSummarizing[idx]) && (
                      <div className="space-y-1.5 mb-3 border border-blue-500/30 bg-blue-500/5 rounded-sm p-2.5" data-testid={`part-ai-summary-block-${idx}`}>
                        <div className="flex items-center justify-between gap-2">
                          <Label className="text-blue-300 text-xs flex items-center gap-1.5 font-semibold">
                            <Sparkles className="w-3 h-3" />
                            Points critiques extraits du plan (IA)
                            {aiSummarizing[idx] && <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
                          </Label>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setAiSummaryLang(prev => ({ ...prev, [idx]: 'fr' }))}
                              className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${(aiSummaryLang[idx] || 'fr') === 'fr' ? 'bg-blue-500/25 text-blue-200 border-blue-500/50' : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-zinc-300'}`}
                              data-testid={`part-ai-lang-fr-${idx}`}
                            >FR</button>
                            <button
                              type="button"
                              onClick={() => setAiSummaryLang(prev => ({ ...prev, [idx]: 'en' }))}
                              className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${aiSummaryLang[idx] === 'en' ? 'bg-blue-500/25 text-blue-200 border-blue-500/50' : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-zinc-300'}`}
                              data-testid={`part-ai-lang-en-${idx}`}
                            >EN</button>
                          </div>
                        </div>
                        <textarea
                          value={(aiSummaryLang[idx] === 'en') ? (part.ai_plan_summary_en || '') : (part.ai_plan_summary_fr || '')}
                          onChange={(e) => updatePart(idx, (aiSummaryLang[idx] === 'en') ? 'ai_plan_summary_en' : 'ai_plan_summary_fr', e.target.value)}
                          rows={Math.min(14, Math.max(4, (((aiSummaryLang[idx] === 'en') ? part.ai_plan_summary_en : part.ai_plan_summary_fr) || '').split('\n').length + 1))}
                          placeholder={aiSummarizing[idx] ? 'Analyse en cours (triple vérification IA, ~60-90s)…' : 'Filetages, tolérances, traitements, cotes critiques…'}
                          className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm px-2 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500/40 resize-y whitespace-pre-wrap"
                          data-testid={`part-ai-summary-textarea-${idx}`}
                        />
                        <div className="flex items-center justify-between text-[10px] text-zinc-500">
                          <span>Éditable · visible côté sous-traitant dans sa langue</span>
                          <button
                            type="button"
                            onClick={() => { updatePart(idx, 'ai_plan_summary_fr', ''); updatePart(idx, 'ai_plan_summary_en', ''); }}
                            className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                            data-testid={`part-ai-summary-clear-${idx}`}
                          >
                            Effacer FR + EN
                          </button>
                        </div>
                      </div>
                    )}

                    {/* File upload per part */}
                    <div className="space-y-1.5">
                      <Label className="text-zinc-500 text-xs">Fichiers STEP / Plans PDF</Label>
                      {/* Show existing attachments when editing — visible only on the FIRST part block to avoid duplication.
                          The backend stores files at the price-request level (not per-part), so we list them once. */}
                      {editingPR && idx === 0 && (editingPR.attachments_meta || []).length > 0 && (
                        <div className="space-y-1 mb-2">
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Déjà attachés</p>
                          {(editingPR.attachments_meta || []).map(f => (
                            <div key={f.id} className="flex items-center gap-2 text-xs text-zinc-400 bg-emerald-500/5 border border-emerald-500/20 rounded-sm px-2.5 py-1">
                              <FileIcon className="w-3 h-3 text-emerald-400" />
                              <span className="truncate flex-1">{f.original_filename}</span>
                              <span className="text-zinc-600">{((f.size || 0) / 1024).toFixed(0)} KB</span>
                              <button
                                type="button"
                                title="Supprimer ce fichier"
                                onClick={async () => {
                                  if (!window.confirm(`Supprimer "${f.original_filename}" ?`)) return;
                                  try {
                                    await api.delete(`/files/${f.id}`);
                                    setEditingPR(prev => prev ? { ...prev, attachments_meta: (prev.attachments_meta || []).filter(x => x.id !== f.id) } : prev);
                                    toast.success('Fichier supprimé');
                                  } catch { toast.error('Erreur suppression'); }
                                }}
                                className="text-zinc-600 hover:text-rose-400"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                          <Upload className="w-3.5 h-3.5" /> Ajouter fichier(s)
                          <input type="file" multiple className="hidden" accept=".step,.stp,.iges,.igs,.pdf,.dwg,.dxf,.stl,.3mf,.png,.jpg,.jpeg" onChange={(e) => handlePartFiles(idx, e.target.files)} />
                        </label>
                        <span className="text-[10px] text-zinc-600">.step .pdf .dwg .stl .png .jpg</span>
                        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none" title="Si coché, l'IA analyse automatiquement le PDF/image dès l'upload. Sinon, tu peux lancer l'analyse manuellement.">
                          <input
                            type="checkbox"
                            checked={aiAutoAnalyze}
                            onChange={(e) => setAiAutoAnalyze(e.target.checked)}
                            className="h-3.5 w-3.5 accent-amber-500 cursor-pointer"
                            data-testid="toggle-ai-auto-analyze"
                          />
                          <Sparkles className="w-3 h-3 text-blue-400" /> Analyse IA à l'upload
                        </label>
                      </div>
                      {partFiles[idx]?.length > 0 && (
                        <div className="space-y-1 mt-1.5">
                          {partFiles[idx].map((f, fi) => {
                            const isAnalyzable = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'].some(x => (f.name || '').toLowerCase().endsWith(x));
                            return (
                              <div key={fi} className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-900/50 border border-zinc-800/50 rounded-sm px-2.5 py-1">
                                <FileIcon className="w-3 h-3 text-zinc-600" />
                                <span className="truncate flex-1">{f.name}</span>
                                <span className="text-zinc-600">{(f.size / 1024).toFixed(0)} KB</span>
                                {isAnalyzable && (
                                  <button
                                    type="button"
                                    onClick={() => handlePartAiSummary(idx, f, { silent: false })}
                                    disabled={aiSummarizing[idx]}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors disabled:opacity-50"
                                    title="Analyser ce plan avec l'IA maintenant"
                                    data-testid={`manual-ai-analyze-${idx}-${fi}`}
                                  >
                                    {aiSummarizing[idx]
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <Sparkles className="w-3 h-3" />}
                                    <span className="text-[10px]">Analyser</span>
                                  </button>
                                )}
                                <button onClick={() => removePartFile(idx, fi)} className="text-zinc-600 hover:text-rose-400"><X className="w-3 h-3" /></button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-price-request" onClick={handleCreate} disabled={uploading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</> : (editingPR ? 'Enregistrer les modifications' : 'Créer la demande')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CLIENT DECLINE DIALOG */}
      <ForwardPRDialog pr={forwardPR} onClose={() => setForwardPR(null)} />

      <Dialog open={!!clientDeclinePR} onOpenChange={(o) => !o && setClientDeclinePR(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="client-decline-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Client à décliner</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Marque cette demande comme "déclinée par le client final". Tous les sous-traitants concernés seront notifiés et ne pourront plus répondre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-zinc-400 text-xs">Raison</Label>
              <select
                value={clientDeclineForm.reason}
                onChange={(e) => setClientDeclineForm(f => ({ ...f, reason: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm h-9 px-3 text-sm mt-1"
                data-testid="client-decline-reason-select"
              >
                <option value="prix_trop_haut">Prix trop élevé</option>
                <option value="delai_trop_long">Délais trop longs</option>
                <option value="projet_annule">Projet annulé</option>
                <option value="autre_fournisseur">Autre fournisseur retenu</option>
                <option value="autre">Autre raison</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Explication (visible par les sous-traitants)</Label>
              <textarea
                value={clientDeclineForm.detail}
                onChange={(e) => setClientDeclineForm(f => ({ ...f, detail: e.target.value }))}
                rows={3}
                placeholder="Ex : Le client a finalement choisi un partenaire historique, merci de ne pas relancer."
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm p-2 text-sm mt-1 resize-none"
                data-testid="client-decline-detail-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientDeclinePR(null)} className="border-zinc-700 text-zinc-300">
              Annuler
            </Button>
            <Button
              onClick={handleSubmitClientDecline}
              disabled={submittingClientDecline}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="client-decline-submit-btn"
            >
              {submittingClientDecline ? 'Envoi…' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detailRequest} onOpenChange={() => setDetailRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{detailRequest?.client_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">{detailRequest?.description}</DialogDescription>
          </DialogHeader>
          {detailRequest && (
            <div className="space-y-4 py-4">
              <div className="flex gap-4 text-xs text-zinc-500">
                {detailRequest.desired_deadline && <span>Délai: {formatDate(detailRequest.desired_deadline)}</span>}
                {detailRequest.certifications?.length > 0 && (
                  <div className="flex gap-1">{detailRequest.certifications.map((c, i) => <span key={i} className="bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm">{c}</span>)}</div>
                )}
              </div>

              {/* Admin-only link */}
              {detailRequest.admin_link && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-3" data-testid="detail-admin-link">
                  <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">Lien interne (admin uniquement)</p>
                  {/^https?:\/\//.test(detailRequest.admin_link) ? (
                    <a href={detailRequest.admin_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300 underline break-all">
                      {detailRequest.admin_link}
                    </a>
                  ) : (
                    <p className="text-sm text-zinc-200 break-words">{detailRequest.admin_link}</p>
                  )}
                </div>
              )}

              {/* Target price — visible to subcontractors */}
              {detailRequest.price_target != null && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3" data-testid="detail-price-target">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1 flex items-center gap-1">
                    <Target className="w-3 h-3" /> Objectif de prix
                  </p>
                  <p className="text-base text-emerald-300 font-bold font-mono">{detailRequest.price_target.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                </div>
              )}

              {/* Quality specs summary */}
              {(detailRequest.general_tolerance || detailRequest.surface_finish || detailRequest.heat_treatment || detailRequest.marking_serialization || detailRequest.inspection_norm || detailRequest.packaging || detailRequest.production_type || detailRequest.additional_specs) && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3" data-testid="detail-quality-specs">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Spécifications qualité</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {detailRequest.general_tolerance && <div><span className="text-zinc-500">Tolérance gén. :</span> <span className="text-zinc-200">{detailRequest.general_tolerance}</span></div>}
                    {detailRequest.surface_finish && <div><span className="text-zinc-500">État surface :</span> <span className="text-zinc-200">{detailRequest.surface_finish}</span></div>}
                    {detailRequest.heat_treatment && <div><span className="text-zinc-500">Traitement therm. :</span> <span className="text-zinc-200">{detailRequest.heat_treatment}</span></div>}
                    {detailRequest.marking_serialization && <div><span className="text-zinc-500">Marquage/série :</span> <span className="text-zinc-200">{detailRequest.marking_serialization}</span></div>}
                    {detailRequest.inspection_norm && <div><span className="text-zinc-500">Inspection :</span> <span className="text-zinc-200">{detailRequest.inspection_norm}</span></div>}
                    {detailRequest.packaging && <div><span className="text-zinc-500">Conditionnement :</span> <span className="text-zinc-200">{detailRequest.packaging}</span></div>}
                    {detailRequest.production_type && <div><span className="text-zinc-500">Type :</span> <span className="text-zinc-200 capitalize">{detailRequest.production_type}</span></div>}
                    {detailRequest.additional_specs && <div className="col-span-2"><span className="text-zinc-500">Autres :</span> <span className="text-zinc-200">{detailRequest.additional_specs}</span></div>}
                  </div>
                </div>
              )}

              {/* Laser marking */}
              {detailRequest.laser_marking_enabled && (
                <div className="bg-sky-500/5 border border-sky-500/20 rounded-sm p-3" data-testid="detail-laser">
                  <p className="text-[10px] uppercase tracking-wider text-sky-400 font-semibold mb-1">Marquage au laser requis</p>
                  {detailRequest.laser_marking_text ? (
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap">{detailRequest.laser_marking_text}</p>
                  ) : (
                    <p className="text-xs text-zinc-500 italic">Texte non spécifié</p>
                  )}
                </div>
              )}

              {/* Parts detail */}
              {detailRequest.parts?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Pièces ({detailRequest.parts.length})</p>
                  <div className="space-y-2">
                    {detailRequest.parts.map((p, i) => (
                      <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-sm font-medium text-zinc-200">{p.name || `Pièce ${i + 1}`}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
                          {p.material && <div><span className="text-zinc-500">Matériau:</span> <span className="text-zinc-300">{MATERIALS[p.material]?.label || p.material}</span></div>}
                          {p.designation && <div><span className="text-zinc-500">Désignation:</span> <span className="text-zinc-300">{p.designation}</span></div>}
                          {p.quantity && <div><span className="text-zinc-500">Qté:</span> <span className="text-zinc-300">{p.quantity}</span></div>}
                          {p.surface_treatment && p.surface_treatment !== 'Aucun' && <div><span className="text-zinc-500">Surface:</span> <span className="text-zinc-300">{p.surface_treatment}</span></div>}
                          {p.color_ral && <div><span className="text-zinc-500">RAL/Couleur:</span> <span className="text-amber-400 font-medium">{p.color_ral}</span></div>}
                          {p.tolerance && <div><span className="text-zinc-500">Tolérance:</span> <span className="text-zinc-300">{p.tolerance}</span></div>}
                          {p.process && <div><span className="text-zinc-500">Procédé:</span> <span className="text-zinc-300">{p.process}</span></div>}
                        </div>
                        {(p.special_points || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-zinc-800/70">
                            <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1 font-semibold">⚠ Points particuliers</p>
                            <div className="flex flex-wrap gap-1">
                              {(p.special_points || []).map(spKey => {
                                const meta = getSpecialPoint(spKey, 'fr');
                                if (!meta) return null;
                                return (
                                  <span key={spKey} className={`text-[11px] px-2 py-0.5 rounded-sm border ${meta.cls.on}`} title={meta.hint}>
                                    {meta.label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {p.notes && <p className="text-xs text-zinc-500 mt-2">{p.notes}</p>}
                        {(p.ai_plan_summary_fr || p.ai_plan_summary_en) && (
                          <div className="mt-2 pt-2 border-t border-blue-500/20 bg-blue-500/5 -mx-3 -mb-3 px-3 pb-2.5 rounded-b-sm">
                            <p className="text-[10px] uppercase tracking-wider text-blue-400 mb-1.5 font-bold flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Points critiques du plan (IA) · FR
                            </p>
                            <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">{p.ai_plan_summary_fr || p.ai_plan_summary_en}</pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {detailFiles.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Fichiers joints ({detailFiles.length})</p>
                  <FileList files={detailFiles} onDelete={() => fetchDetailFiles(detailRequest.id)} />
                </div>
              )}

              {/* Responses with ranking */}
              <ResponseRanking
                detailRequest={detailRequest}
                fmtCur={fmtCur}
                onAssigned={() => { setDetailRequest(null); fetchRequests(); }}
                onNegotiate={async () => {
                  // Refresh only the current PR to update negotiation counts, keep dialog open
                  try {
                    const { data } = await api.get(`/price-requests/${detailRequest.id}`);
                    setDetailRequest(data);
                    fetchRequests();
                  } catch { /* silent */ }
                }}
              />

              {/* Declined subcontractors */}
              {detailRequest.declines?.length > 0 && (
                <div>
                  <p className="text-xs text-rose-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5" /> Sous-traitants ayant décliné ({detailRequest.declines.length})
                  </p>
                  <div className="space-y-1.5">
                    {detailRequest.declines.map((d, i) => (
                      <div key={i} className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-2.5" data-testid={`decline-row-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-rose-300">{d.sub_name}</span>
                          <span className="text-[10px] text-zinc-500">{formatDate(d.declined_at)}</span>
                        </div>
                        {d.reason ? (
                          <p className="text-xs text-zinc-400 italic mt-1">"{d.reason}"</p>
                        ) : (
                          <p className="text-xs text-zinc-600 italic mt-1">(aucune raison fournie)</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick actions inside the detail dialog — mirrors the card buttons for fast workflow */}
              {detailRequest.status !== 'trashed' && (
                <div className="pt-3 mt-2 border-t border-zinc-800 flex flex-wrap gap-2" data-testid="detail-actions">
                  <Button size="sm" variant="outline" onClick={() => { setDetailRequest(null); openChat(detailRequest); }} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid="detail-action-message">
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Messages
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDetailRequest(null); openEdit(detailRequest); }} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid="detail-action-edit">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDetailRequest(null); setForwardPR(detailRequest); }} className="border-blue-500/30 bg-blue-500/5 text-blue-300 hover:bg-blue-500/10 rounded-sm text-xs h-8" data-testid="detail-action-forward">
                    <Forward className="w-3.5 h-3.5 mr-1" /> Transférer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setBroadcastPR(detailRequest); setDetailRequest(null); }} className="border-amber-500/30 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10 rounded-sm text-xs h-8" data-testid="detail-action-broadcast">
                    <Megaphone className="w-3.5 h-3.5 mr-1" /> Envoyer aux sous-traitants
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setBroadcastLogPR(detailRequest)} className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid="detail-action-broadcast-log">
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Historique d'envoi
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const already = !!detailRequest.order_confirmed_at;
                      const msg = already
                        ? 'Renvoyer la confirmation de commande au sous-traitant ?'
                        : 'Envoyer le feu vert "GO PRODUCTION" au sous-traitant ?\n\nIl recevra un email + une notif dans son portail confirmant qu\'il peut lancer sans autre validation.';
                      if (!window.confirm(msg)) return;
                      try {
                        const { data } = await api.post(`/price-requests/${detailRequest.id}/confirm-order`);
                        toast.success(already ? '✓ Confirmation renvoyée' : `✅ GO PRODUCTION envoyé à ${data.sub_email || 'sous-traitant'}`);
                        fetchRequests();
                        setDetailRequest(null);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Erreur');
                      }
                    }}
                    className={detailRequest.order_confirmed_at
                      ? "bg-emerald-950 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-900 rounded-sm text-xs h-8"
                      : "bg-emerald-500 text-white hover:bg-emerald-400 rounded-sm text-xs h-8 font-semibold"}
                    data-testid="detail-action-confirm-order"
                  >
                    {detailRequest.order_confirmed_at ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <span className="mr-1">🟢</span>}
                    {detailRequest.order_confirmed_at ? 'Renvoyer la confirmation' : 'Confirmer la commande'}
                  </Button>
                  {detailRequest.status !== 'proposition_faite' && detailRequest.status !== 'closed' && detailRequest.status !== 'client_declined' && (
                    <Button size="sm" variant="outline" onClick={() => { handleProposalMade(detailRequest.id); setDetailRequest(null); }} className="border-violet-500/30 bg-violet-500/5 text-violet-300 hover:bg-violet-500/10 rounded-sm text-xs h-8" data-testid="detail-action-proposal">
                      Proposition faite
                    </Button>
                  )}
                  {detailRequest.status !== 'closed' && detailRequest.status !== 'client_declined' && (
                    <Button size="sm" variant="outline" onClick={() => { setDetailRequest(null); setClientDeclinePR(detailRequest); }} className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid="detail-action-client-decline">
                    Client à décliner
                  </Button>
                  )}
                  <div className="flex-1" />
                  <Button size="sm" variant="outline" onClick={() => { handleDelete(detailRequest.id); }} className="border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid="detail-action-delete">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CHAT DIALOG (admin) */}
      <Dialog open={!!chatRequest} onOpenChange={() => setChatRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[85vh] overflow-hidden p-0" data-testid="pr-chat-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Messages — {chatRequest?.client_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Échangez avec les sous-traitants pour clarifier la demande
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-0 h-[60vh]">
            {/* Threads list */}
            <div className="border-r border-zinc-800 overflow-y-auto bg-zinc-950/50">
              {threads.length === 0 ? (
                <p className="p-4 text-xs text-zinc-500 text-center">Aucune conversation. Les sous-traitants peuvent initier la conversation depuis leur portail.</p>
              ) : threads.map(t => (
                <button
                  key={t.subcontractor_id}
                  onClick={() => selectThread(chatRequest.id, t.subcontractor_id)}
                  data-testid={`thread-${t.subcontractor_id}`}
                  className={`w-full text-left p-3 border-b border-zinc-800/50 transition-colors ${activeThreadSubId === t.subcontractor_id ? 'bg-amber-500/10 border-l-2 border-l-amber-500' : 'hover:bg-zinc-900'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-zinc-100 truncate">{t.subcontractor_name || 'Sous-traitant'}</span>
                    <div className="flex items-center gap-1">
                      {t.unread_for_admin > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold bg-rose-500 text-zinc-50 animate-pulse" data-testid={`thread-unread-${t.subcontractor_id}`}>{t.unread_for_admin}</span>
                      )}
                      {t.message_count > 0 && (
                        <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm">{t.message_count}</span>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500 truncate">{t.last_text || '(pas encore de message)'}</p>
                  {t.last_message_at && <p className="text-[10px] text-zinc-600 mt-0.5">{formatDate(t.last_message_at)}</p>}
                </button>
              ))}
            </div>
            {/* Chat panel */}
            <div className="col-span-2 flex flex-col min-h-0">
              {!activeThreadSubId ? (
                <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
                  Sélectionnez un sous-traitant à gauche
                </div>
              ) : (
                <>
                  <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2" data-testid="pr-chat-messages">
                    {chatMessages.length === 0 ? (
                      <p className="text-xs text-zinc-600 text-center py-8">Aucun message. Démarrez la conversation.</p>
                    ) : chatMessages.map(m => {
                      const mine = m.sender_role === 'admin';
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                            <p className="text-[10px] text-zinc-500 mb-0.5">
                              <span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                              <span className="ml-2">{formatDate(m.created_at)}</span>
                            </p>
                            {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                            {(m.attachments_meta || []).length > 0 && (
                              <div className="mt-1.5 space-y-1">
                                {m.attachments_meta.map(f => (
                                  <button key={f.id} onClick={() => setPreviewFile(f)} className="flex items-center gap-1.5 text-[11px] text-blue-400 hover:text-blue-300 underline" data-testid={`attachment-${f.id}`}>
                                    <FileIcon className="w-3 h-3" /> {f.original_filename} <span className="text-zinc-600">({((f.size || 0) / 1024).toFixed(0)} KB)</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-zinc-800 p-3 bg-zinc-950/50 space-y-2">
                    {chatPendingFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="pr-chat-pending-files">
                        {chatPendingFiles.map(f => (
                          <span key={f.id} className="inline-flex items-center gap-1 text-[11px] bg-zinc-900 border border-zinc-800 text-zinc-300 px-2 py-1 rounded-sm">
                            <FileIcon className="w-3 h-3" /> {f.original_filename}
                            <button onClick={() => removePendingFile(f.id)} className="ml-1 text-zinc-500 hover:text-rose-400"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="flex items-center justify-center w-9 h-9 bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors" title="Joindre des fichiers (STEP, PDF, images, etc.)">
                        {chatUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        <input data-testid="pr-chat-file-input" type="file" multiple className="hidden" onChange={(e) => uploadChatFiles(e.target.files)} disabled={chatUploading} />
                      </label>
                      <Input data-testid="pr-chat-input" value={newChatText} onChange={(e) => setNewChatText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendChatMessage())}
                        placeholder="Écrire un message..."
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" />
                      <Button data-testid="pr-chat-send" onClick={sendChatMessage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <FilePreviewDialog file={previewFile} open={!!previewFile} onOpenChange={(v) => !v && setPreviewFile(null)} />
      <BroadcastSubcontractorsDialog
        pr={broadcastPR}
        open={!!broadcastPR}
        onClose={() => setBroadcastPR(null)}
      />
      <BroadcastLogDialog
        pr={broadcastLogPR}
        open={!!broadcastLogPR}
        onClose={() => setBroadcastLogPR(null)}
      />

      {/* Merge confirmation dialog */}
      <Dialog open={mergeDialog} onOpenChange={(v) => { if (!v) { setMergeDialog(false); setMergePrimaryId(''); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Box className="w-4 h-4 text-amber-400" />
              Fusionner {mergeSelected.size} demandes
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Choisis la demande <span className="text-amber-300 font-semibold">primaire</span> — elle conservera son ID et recevra les pièces + offres des autres. Les demandes secondaires seront <span className="text-rose-300 font-semibold">supprimées définitivement</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto py-2">
            {Array.from(mergeSelected).map(id => {
              const req = requests.find(r => r.id === id);
              if (!req) return null;
              const isPrimary = mergePrimaryId === id;
              return (
                <label key={id} className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${isPrimary ? 'bg-amber-500/10 border-amber-500/40' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-700'}`} data-testid={`merge-primary-option-${id}`}>
                  <input
                    type="radio"
                    name="merge-primary"
                    value={id}
                    checked={isPrimary}
                    onChange={() => setMergePrimaryId(id)}
                    className="w-4 h-4 mt-0.5 accent-amber-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-zinc-100 truncate">{req.client_name}</span>
                      {isPrimary && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold uppercase tracking-wider">Primaire</span>}
                    </div>
                    <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3">
                      <span>Réf {req.reference || req.id.slice(0, 8)}</span>
                      <span>{(req.parts || []).length} pièce(s)</span>
                      <span>{req.response_count || 0} offre(s)</span>
                      <span>Statut {req.status}</span>
                    </div>
                    {req.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{req.description}</p>}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMergeDialog(false); setMergePrimaryId(''); }} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="merge-cancel-btn">
              Annuler
            </Button>
            <Button
              onClick={async () => {
                if (!mergePrimaryId || mergeSelected.size < 2) return;
                const secondaries = Array.from(mergeSelected).filter(id => id !== mergePrimaryId);
                if (!window.confirm(`Fusionner ${secondaries.length} demande(s) dans la primaire ?\nLes secondaires seront SUPPRIMÉES DÉFINITIVEMENT.`)) return;
                setMerging(true);
                try {
                  const { data } = await api.post('/price-requests/merge', {
                    primary_id: mergePrimaryId,
                    secondary_ids: secondaries,
                  });
                  toast.success(`✅ Fusion réussie — ${data.merged_count} demandes fusionnées · ${data.parts_added} pièces + ${data.responses_moved} offres transférées`);
                  setMergeDialog(false);
                  setMergeMode(false);
                  setMergeSelected(new Set());
                  setMergePrimaryId('');
                  fetchRequests();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Erreur lors de la fusion');
                } finally { setMerging(false); }
              }}
              disabled={!mergePrimaryId || merging}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-9 font-semibold disabled:opacity-40"
              data-testid="merge-confirm-btn"
            >
              {merging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Box className="w-4 h-4 mr-1.5" />}
              Confirmer la fusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProposalMadeDialog
        pr={proposalPR}
        open={!!proposalPR}
        onClose={() => setProposalPR(null)}
        onDone={() => { setProposalPR(null); fetchRequests(); }}
      />
    </div>
  );
}

function BroadcastSubcontractorsDialog({ pr, open, onClose }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterSpec, setFilterSpec] = useState('all');
  const [ndaOnly, setNdaOnly] = useState(false);

  useEffect(() => {
    if (!open || !pr) return;
    setLoading(true);
    api.get('/price-requests/eligible-subcontractors/list')
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setSubs(list);
        // Pre-select all active subs by default
        setSelected(new Set(list.filter(s => s.is_active).map(s => s.id)));
      })
      .catch(() => toast.error('Erreur chargement sous-traitants'))
      .finally(() => setLoading(false));
  }, [open, pr]);

  // Build filter option lists (countries, specialties)
  const countries = Array.from(new Set(subs.map(s => s.country).filter(Boolean))).sort();
  const specialties = Array.from(new Set(subs.map(s => s.specialty).filter(Boolean))).sort();

  const filtered = subs.filter(s => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${s.company || ''} ${s.name || ''} ${s.email || ''} ${s.specialty || ''} ${s.city || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterCountry !== 'all' && s.country !== filterCountry) return false;
    if (filterSpec !== 'all' && s.specialty !== filterSpec) return false;
    if (ndaOnly && !s.nda_signed) return false;
    return true;
  });

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = (check) => {
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(s => { if (check) next.add(s.id); else next.delete(s.id); });
      return next;
    });
  };

  const handleSend = async () => {
    const ids = Array.from(selected);
    if (!ids.length) { toast.error('Sélectionnez au moins 1 sous-traitant'); return; }
    if (!window.confirm(`Envoyer la demande à ${ids.length} sous-traitant${ids.length > 1 ? 's' : ''} ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/price-requests/${pr.id}/send-to-subcontractors`, { subcontractor_ids: ids });
      const sent = Number(data.sent || 0);
      const selectedCount = Number(data.selected || ids.length);
      const results = Array.isArray(data.results) ? data.results : [];
      const errors = results.filter(r => r.status === 'error');
      if (sent === selectedCount && sent > 0) {
        toast.success(`✉️ Envoyé à ${sent}/${selectedCount} sous-traitants`);
      } else if (sent > 0) {
        const errMails = errors.slice(0, 3).map(r => r.email || '?').join(', ');
        toast.warning(`⚠ Envoyé à ${sent}/${selectedCount}. Échecs: ${errors.length}${errMails ? ` (${errMails}${errors.length > 3 ? '…' : ''})` : ''}`, { duration: 8000 });
      } else if (data.error) {
        const errMsg = {
          resend_not_configured: 'Resend non configuré (clé API manquante). Contactez l\'administrateur.',
          resend_config_unavailable: 'Configuration email indisponible.',
          email_builders_unavailable: 'Erreur interne (email builder).',
        }[data.error] || data.error;
        toast.error(`❌ 0/${selectedCount} envoyé — ${errMsg}`, { duration: 10000 });
      } else if (errors.length > 0) {
        const firstErr = errors[0];
        toast.error(`❌ 0/${selectedCount} envoyé — ${firstErr.reason || 'échec Resend'} (${firstErr.email})`, { duration: 10000 });
      } else {
        toast.error(`❌ 0/${selectedCount} envoyé — Aucun destinataire trouvé (IDs invalides ?). Ouvrez la console ou l\'historique d\'envoi.`, { duration: 10000 });
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every(s => selected.has(s.id));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="broadcast-subs-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-300">📨 Envoyer aux sous-traitants</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Demande « {pr?.title || pr?.client_name || ''} » — chaque sous-traitant coché recevra un email avec tous les détails et pièces jointes.
            Décochez ceux à exclure.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="text"
              placeholder="Rechercher nom, email, ville…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600"
              data-testid="broadcast-search"
            />
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100"
              data-testid="broadcast-country-filter"
            >
              <option value="all">Tous pays</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filterSpec}
              onChange={(e) => setFilterSpec(e.target.value)}
              className="bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100"
              data-testid="broadcast-spec-filter"
            >
              <option value="all">Toutes spécialités</option>
              {specialties.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-zinc-300 px-2">
              <input
                type="checkbox"
                checked={ndaOnly}
                onChange={(e) => setNdaOnly(e.target.checked)}
                className="accent-amber-500"
                data-testid="broadcast-nda-only"
              />
              NDA signée uniquement
            </label>
          </div>

          {/* Quick actions */}
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <span data-testid="broadcast-counts">
              {filtered.length} affiché{filtered.length > 1 ? 's' : ''} · <strong className="text-amber-300">{selected.size}</strong> sélectionné{selected.size > 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <button onClick={() => toggleAllVisible(true)} className="text-emerald-400 hover:underline" data-testid="broadcast-select-all">Tout cocher</button>
              <span className="text-zinc-700">·</span>
              <button onClick={() => toggleAllVisible(false)} className="text-zinc-500 hover:underline" data-testid="broadcast-deselect-all">Tout décocher</button>
            </div>
          </div>

          {/* List */}
          <div className="border border-zinc-800 rounded-sm max-h-[45vh] overflow-y-auto" data-testid="broadcast-sub-list">
            {loading ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">Aucun sous-traitant ne correspond aux filtres</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-950/50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => toggleAllVisible(e.target.checked)}
                        className="accent-amber-500"
                        data-testid="broadcast-header-checkbox"
                      />
                    </th>
                    <th className="text-left px-2 py-2">Entreprise</th>
                    <th className="text-left px-2 py-2">Spécialité</th>
                    <th className="text-left px-2 py-2">Pays</th>
                    <th className="text-left px-2 py-2">NDA</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => {
                    const checked = selected.has(s.id);
                    return (
                      <tr
                        key={s.id}
                        className={`border-t border-zinc-800 hover:bg-zinc-800/40 cursor-pointer ${checked ? 'bg-amber-500/5' : ''} ${!s.is_active ? 'opacity-60' : ''}`}
                        onClick={() => toggleOne(s.id)}
                        data-testid={`broadcast-row-${s.id}`}
                      >
                        <td className="px-2 py-2">
                          <input type="checkbox" checked={checked} onChange={() => toggleOne(s.id)} className="accent-amber-500" onClick={(e) => e.stopPropagation()} />
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-zinc-200 font-medium">{s.company || s.name || '—'}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{s.email}</div>
                          {!s.is_active && <div className="text-[10px] text-rose-400">Inactif</div>}
                        </td>
                        <td className="px-2 py-2 text-xs text-zinc-400">{s.specialty || '—'}</td>
                        <td className="px-2 py-2 text-xs text-zinc-400">{s.country || '—'}{s.city ? ` · ${s.city}` : ''}</td>
                        <td className="px-2 py-2 text-xs">
                          {s.nda_signed ? <span className="text-emerald-400">✓ Signée</span> : <span className="text-zinc-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="broadcast-cancel-btn">
            Plus tard
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !selected.size}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium"
            data-testid="broadcast-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
            Envoyer à {selected.size} sous-traitant{selected.size > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProposalMadeDialog({ pr, open, onClose, onDone }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !pr) return;
    setLoading(true);
    setSelectedId(pr.best_subcontractor_id || '');
    // pr.responses is already on the admin payload; refetch in case stale
    api.get('/price-requests')
      .then(({ data }) => {
        const fresh = (data || []).find(r => r.id === pr.id);
        setResponses(fresh?.responses || pr.responses || []);
      })
      .catch(() => setResponses(pr.responses || []))
      .finally(() => setLoading(false));
  }, [open, pr]);

  const fmtCur = (n, c) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c || 'EUR'}`;
  const totalOf = (r) => (r.total_price != null ? r.total_price : (r.price_ttc || 0) + (r.transport_cost || 0) + (r.other_fees || 0));

  // Sort by ascending total price (best first)
  const sorted = [...responses].sort((a, b) => totalOf(a) - totalOf(b));

  const handleConfirm = async (withBest) => {
    if (!pr) return;
    setSubmitting(true);
    try {
      const payload = { status: 'proposition_faite' };
      if (withBest && selectedId) payload.best_subcontractor_id = selectedId;
      await api.put(`/price-requests/${pr.id}`, payload);
      if (withBest && selectedId) {
        const winner = responses.find(r => r.subcontractor_id === selectedId);
        toast.success(`✓ Proposition transmise — ${winner?.subcontractor_name || 'sous-traitant'} a été notifié comme mieux placé`);
      } else {
        toast.success('Marquée "Proposition faite" — sous-traitants notifiés');
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="proposal-made-dialog">
        <DialogHeader>
          <DialogTitle className="text-violet-300">📣 Proposition transmise au client</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Demande « {pr?.title || pr?.client_name || ''} » — vous pouvez désigner le sous-traitant que vous comptez retenir.
            Seul ce sous-traitant verra qu'il est <strong className="text-amber-300">mieux placé</strong> — les autres recevront un message neutre, sans visibilité sur les prix concurrents.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {loading ? (
            <div className="p-6 text-center text-zinc-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>
          ) : sorted.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">Aucune réponse reçue pour cette demande.</div>
          ) : (
            <div className="border border-zinc-800 rounded-sm max-h-[50vh] overflow-y-auto" data-testid="proposal-sub-list">
              {sorted.map((r, idx) => {
                const checked = selectedId === r.subcontractor_id;
                return (
                  <label
                    key={r.id}
                    className={`flex items-center gap-3 px-3 py-3 border-b border-zinc-800 last:border-b-0 cursor-pointer hover:bg-zinc-800/40 ${checked ? 'bg-amber-500/10 border-l-2 border-l-amber-500' : ''}`}
                    data-testid={`proposal-row-${r.id}`}
                  >
                    <input
                      type="radio"
                      name="best_sub"
                      checked={checked}
                      onChange={() => setSelectedId(r.subcontractor_id)}
                      className="accent-amber-500"
                      data-testid={`proposal-radio-${r.id}`}
                    />
                    <div className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-bold flex-shrink-0 ${idx === 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-zinc-800 text-zinc-500'}`}>
                      {idx === 0 ? 'TOP' : `#${idx + 1}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 font-medium flex items-center gap-2">
                        {r.subcontractor_name}
                        {r.source === 'email_auto' && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-sky-500/10 text-sky-300 border-sky-500/30">📬 Auto</span>
                        )}
                        {r.needs_review && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-500/10 text-amber-300 border-amber-500/40">⚠ À vérifier</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{r.observations || r.quote_number || '—'}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-bold ${idx === 0 ? 'text-amber-300' : 'text-zinc-300'}`}>
                        {fmtCur(totalOf(r), r.currency)}
                      </div>
                      <div className="text-[10px] text-zinc-500">{r.deadline || '—'}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="proposal-cancel-btn">
            Annuler
          </Button>
          <Button
            variant="outline"
            onClick={() => handleConfirm(false)}
            disabled={submitting}
            className="border-zinc-700 text-zinc-400 rounded-sm"
            data-testid="proposal-skip-btn"
            title="Marquer comme proposée sans désigner de mieux placé"
          >
            Sans désigner
          </Button>
          <Button
            onClick={() => handleConfirm(true)}
            disabled={submitting || !selectedId}
            className="bg-violet-500 text-white hover:bg-violet-400 rounded-sm font-medium"
            data-testid="proposal-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Megaphone className="w-4 h-4 mr-1.5" />}
            Confirmer le mieux placé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResponseRanking({ detailRequest, fmtCur, onAssigned, onNegotiate }) {
  const [sortBy, setSortBy] = useState('price');
  const [previewFileId, setPreviewFileId] = useState(null);
  const [negotiateResp, setNegotiateResp] = useState(null);
  const responses = detailRequest?.responses || [];
  const totalOf = (r) => (r.total_price != null ? r.total_price : (r.price_ttc || 0) + (r.transport_cost || 0) + (r.other_fees || 0));
  const sorted = [...responses].sort((a, b) => {
    if (sortBy === 'price') return totalOf(a) - totalOf(b);
    if (sortBy === 'deadline') return (a.deadline || '').localeCompare(b.deadline || '');
    return 0;
  });

  const handleAssign = async (response) => {
    if (!window.confirm(`Attribuer la commande à ${response.subcontractor_name} pour ${fmtCur(totalOf(response), response.currency)} ?`)) return;
    try {
      await api.post(`/price-requests/${detailRequest.id}/assign`, { response_id: response.id });
      toast.success(`Commande attribuée à ${response.subcontractor_name}`);
      onAssigned();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const downloadAttachment = async (fileId) => {
    try {
      const response = await api.get(`/files/download/${fileId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis-${fileId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur de téléchargement'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-zinc-200">{responses.length} offre(s) reçue(s)</p>
        {responses.length > 1 && (
          <div className="flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-sm p-0.5 text-xs">
            <button onClick={() => setSortBy('price')} className={`px-2.5 py-1 rounded-sm transition-colors ${sortBy === 'price' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
              Meilleur prix total
            </button>
            <button onClick={() => setSortBy('deadline')} className={`px-2.5 py-1 rounded-sm transition-colors ${sortBy === 'deadline' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
              Délai
            </button>
          </div>
        )}
      </div>
      {sorted.length > 0 ? (
        <div className="space-y-2">
          {sorted.map((r, idx) => (
            <div key={r.id} className={`p-3 rounded-sm border ${idx === 0 ? 'bg-amber-500/5 border-amber-500/20' : 'bg-zinc-950 border-zinc-800'}`} data-testid={`response-${r.id}`}>
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-sm font-bold flex-shrink-0 ${idx === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'}`}>
                  {idx === 0 ? 'TOP' : `#${idx + 1}`}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                    {r.subcontractor_name}
                    {r.source === 'email_auto' && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-sky-500/10 text-sky-300 border-sky-500/30" data-testid={`badge-email-auto-${r.id}`}>
                        📬 Auto-saisi
                      </span>
                    )}
                    {r.needs_review && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-500/10 text-amber-300 border-amber-500/40 animate-pulse" data-testid={`badge-needs-review-${r.id}`} title={`Confiance d'extraction: ${r.extraction_confidence || 'low'}`}>
                        ⚠ À vérifier
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-zinc-500">{r.observations || '-'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-bold ${idx === 0 ? 'text-amber-400' : 'text-zinc-300'}`} data-testid={`response-total-${r.id}`}>
                    {fmtCur(totalOf(r), r.currency)}
                  </p>
                  <p className="text-[10px] text-zinc-500">{r.deadline || '-'}</p>
                </div>
                {detailRequest.status === 'open' && (
                  <Button size="sm" onClick={() => handleAssign(r)} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm text-xs h-8 font-medium flex-shrink-0" data-testid={`assign-${r.id}`}>
                    Attribuer
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setNegotiateResp(r)} className="bg-amber-500/5 text-amber-300 border-amber-500/30 hover:bg-amber-500/15 rounded-sm text-xs h-8 font-medium flex-shrink-0 relative" data-testid={`negotiate-${r.id}`}>
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Négocier
                  {(r.negotiations || []).length > 0 && (
                    <span className="ml-1.5 bg-amber-500/30 text-amber-100 rounded-full px-1.5 py-0 text-[10px] font-bold" data-testid={`negotiation-count-${r.id}`}>
                      {(r.negotiations || []).length}
                    </span>
                  )}
                </Button>
              </div>
              {/* Breakdown */}
              {((r.transport_cost || 0) > 0 || (r.other_fees || 0) > 0 || (r.attachments || []).length > 0) && (
                <div className="mt-2 pt-2 border-t border-zinc-800/60 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
                  <div><span className="text-zinc-500">Prix HT/TTC:</span> <span className="text-zinc-300">{fmtCur(r.price_ttc, r.currency)}</span></div>
                  {(r.transport_cost || 0) > 0 && <div><span className="text-zinc-500">Transport:</span> <span className="text-zinc-300">{fmtCur(r.transport_cost, r.currency)}</span></div>}
                  {(r.other_fees || 0) > 0 && <div><span className="text-zinc-500">Autres frais:</span> <span className="text-zinc-300">{fmtCur(r.other_fees, r.currency)}</span></div>}
                  {(r.attachments || []).length > 0 && (
                    <div className="col-span-2 md:col-span-4">
                      <span className="text-zinc-500">Devis joint: </span>
                      {r.attachments.map((fileId, i) => (
                        <button key={fileId} onClick={() => setPreviewFileId(fileId)} className="text-blue-400 hover:text-blue-300 underline mr-2" data-testid={`attachment-${r.id}-${i}`}>
                          Voir le devis {i + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-500 text-center py-4">Aucune offre reçue</p>}
      <FilePreviewDialog fileId={previewFileId} open={!!previewFileId} onOpenChange={(v) => !v && setPreviewFileId(null)} />
      <NegotiationDialog
        open={!!negotiateResp}
        onOpenChange={(v) => !v && setNegotiateResp(null)}
        viewerRole="admin"
        prId={detailRequest?.id}
        responseId={negotiateResp?.id}
        initialPrice={negotiateResp ? (negotiateResp.total_price != null ? negotiateResp.total_price : (negotiateResp.price_ttc || 0) + (negotiateResp.transport_cost || 0) + (negotiateResp.other_fees || 0)) : null}
        currency={negotiateResp?.currency || 'EUR'}
        subLabel={negotiateResp?.subcontractor_name || ''}
        clientLabel={detailRequest?.client_name || ''}
        onChange={onNegotiate || onAssigned}
      />
    </div>
  );
}


function BroadcastLogDialog({ pr, open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState({ broadcast_log: [], manually_sent_to: [], manually_sent_at: null, last_broadcast_at: null });

  useEffect(() => {
    if (!open || !pr) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/price-requests/${pr.id}/broadcast-log`);
        if (mounted) setLog(data || { broadcast_log: [] });
      } catch {
        if (mounted) setLog({ broadcast_log: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, pr]);

  const entries = (log.broadcast_log || []).slice().reverse(); // newest first
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };
  const statusPill = (status) => {
    const map = {
      sent: { label: 'Envoyé', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
      error: { label: 'Échec', cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
      skipped: { label: 'Ignoré', cls: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40' },
    };
    const c = map[status] || map.skipped;
    return <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-medium ${c.cls}`}>{c.label}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="broadcast-log-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-amber-400" />
            Historique d'envoi — {pr?.client_name || pr?.reference || '—'}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Détail complet des envois par email pour cette demande. Utile pour vérifier ce qui est vraiment parti.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center py-8 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Chargement…</div>}
        {!loading && entries.length === 0 && (
          <div className="text-center py-8 text-zinc-500 text-sm">Aucun envoi enregistré pour cette demande.</div>
        )}
        {!loading && entries.map((entry, i) => (
          <div key={i} className="border border-zinc-800 rounded-sm bg-zinc-950 p-3 mb-2" data-testid={`broadcast-entry-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-400">
                {fmtDate(entry.at)} · {entry.forced ? 'Envoi manuel' : 'Envoi automatique'}
              </span>
              <span className={`text-xs font-mono px-2 py-0.5 rounded-sm border ${entry.sent === entry.total && entry.sent > 0
                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                : entry.sent > 0
                  ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                  : 'bg-rose-500/10 text-rose-300 border-rose-500/30'}`}>
                {entry.sent}/{entry.total} envoyé{entry.sent > 1 ? 's' : ''}
              </span>
            </div>
            {entry.error && <div className="mb-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-sm px-2 py-1">Erreur globale : {entry.error}</div>}
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {(entry.results || []).map((r, j) => (
                <div key={j} className="flex items-center justify-between gap-2 text-xs bg-zinc-900 rounded-sm px-2 py-1" data-testid={`broadcast-result-${i}-${j}`}>
                  <span className="text-zinc-300 truncate">{r.email || '—'}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.reason && <span className="text-zinc-500 text-[10px] truncate max-w-[280px]" title={r.reason}>{r.reason.slice(0, 60)}</span>}
                    {statusPill(r.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="broadcast-log-close-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
